function getMetadata(name) {
  return (
    document
      .querySelector(`meta[property='${name}']`)
      ?.getAttribute("content") ||
    document.querySelector(`meta[name='${name}']`)?.getAttribute("content") ||
    ""
  );
}

function normalizeTemplate(template) {
  return template?.toLowerCase().trim() || "";
}

function cleanText(text) {
  return text?.replace(/\s+/g, " ").trim() || "";
}

function toISODate(date) {
  if (!date) return "";
  return new Date(date).toISOString();
}

function observeDynamicContent(selector, callback, debounceDelay = 500) {
  let debounceTimeout;
  const observedElements = new Set();

  const processElements = () => {
    const elements = document.querySelectorAll(selector);

    elements.forEach(item => {
      observedElements.add(item);
    });

    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      callback(Array.from(observedElements));
    }, debounceDelay);
  };

  const observer = new MutationObserver(() => {
    processElements();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  processElements();
}

function convertHoursToISO8601Duration(hours) {
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  let isoDuration = "PT";
  if (h > 0) isoDuration += `${h}H`;
  if (m > 0) isoDuration += `${m}M`;
  if (h === 0 && m === 0) isoDuration += "0H";
  return isoDuration;
}

function parseLocalTimeIgnoringOffset(dateString) {
  const trimmed = dateString.replace(/([+-]\d{2}:\d{2}|Z)$/, "");
  return new Date(`${trimmed}Z`);
}

function calculateCourseWorkload(startDate, endDate) {
  if (!startDate || !endDate) return "";
  const startTime = parseLocalTimeIgnoringOffset(startDate);
  const endTime = parseLocalTimeIgnoringOffset(endDate);
  if (Number.isNaN(startTime.getTime()) || Number.isNaN(endTime.getTime()))
    return "";
  const durationInMs = endTime - startTime;
  if (durationInMs <= 0) return "";
  const durationInHours = (durationInMs / (1000 * 60 * 60)).toFixed(1);
  return convertHoursToISO8601Duration(durationInHours);
}

function extractCourseSchedule(dateText) {
  if (!dateText || typeof dateText !== "string") return null;
  const byDay = dateText.match(/^(\w+),/);
  const startDate = dateText.match(/^[^,]+,\s([^,]+,\s\d{4})/);
  const startTime = dateText.match(/,\s(\d{1,2}:\d{2}(?:am|pm))/i);
  const endTime = dateText.match(/-\s(\d{1,2}:\d{2}(?:am|pm))/i);
  if (!byDay || !startDate || !startTime || !endTime) return null;
  return {
    "@type": "Schedule",
    byDay: byDay[1].trim(),
    startDate: startDate[1].trim(),
    startTime: startTime[1].trim(),
    endTime: endTime[1].trim(),
    repeatFrequency: "P0D",
    repeatCount: 1,
  };
}

function extractCourseDateRange(dateText) {
  if (!dateText || typeof dateText !== "string") {
    return { startDate: "", endDate: "" };
  }

  const MONTHS = {
    january: 1,
    february: 2,
    march: 3,
    april: 4,
    may: 5,
    june: 6,
    july: 7,
    august: 8,
    september: 9,
    october: 10,
    november: 11,
    december: 12,
  };

  const pad = n => String(n).padStart(2, "0");

  const to24Hour = (h, m, ampm) => {
    let hour = parseInt(h, 10);
    if (ampm.toLowerCase() === "pm" && hour !== 12) hour += 12;
    if (ampm.toLowerCase() === "am" && hour === 12) hour = 0;
    return `${pad(hour)}:${m}`;
  };

  const normalized = cleanText(dateText)
    .replace(/(\d+)(st|nd|rd|th)\b/gi, "$1")
    .replace(/\u2013/g, "-");

  const match = normalized.match(
    /(?:\w+,\s+)?(\w+)\s+(\d+),\s+(\d{4}),\s+(\d{1,2}):(\d{2})\s*(am|pm)\s*-\s*(\d{1,2}):(\d{2})\s*(am|pm)/i,
  );

  if (!match) return { startDate: "", endDate: "" };

  const [, monthName, day, year, sh, sm, sampm, eh, em, eampm] = match;
  const month = MONTHS[monthName.toLowerCase()];
  if (!month) return { startDate: "", endDate: "" };

  const datePrefix = `${year}-${pad(month)}-${pad(parseInt(day, 10))}`;

  return {
    startDate: `${datePrefix}T${to24Hour(sh, sm, sampm)}`,
    endDate: `${datePrefix}T${to24Hour(eh, em, eampm)}`,
  };
}

async function getGeoCoordinates(mapUrl) {
  try {
    const decodedUrl = decodeURIComponent(mapUrl || "");
    const addressMatch = decodedUrl.match(/place\/([^/]+)/);

    if (!addressMatch || addressMatch.length < 2) {
      throw new Error("Address could not be extracted from the URL.");
    }

    let address = addressMatch[1].replace(/\+/g, " ").replace(/-/g, " ");
    address = address.replace(/Ste\..*$/, "").trim();

    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
        address,
      )}&format=json`,
    );

    if (!response.ok) {
      throw new Error(`API responded with status: ${response.status}`);
    }

    const data = await response.json();
    if (data && data.length > 0) {
      return {
        latitude: parseFloat(data[0].lat) || "",
        longitude: parseFloat(data[0].lon) || "",
      };
    }
  } catch (error) {
    // Keep schema generation resilient even when geocoding fails.
  }

  return { latitude: "", longitude: "" };
}

function buildFilteredSearchParams() {
  const mountEl = document.querySelector(".js-search-mount");
  const queryData = mountEl?.dataset?.query
    ? JSON.parse(mountEl.dataset.query)
    : null;
  const pageId = queryData?.pageId || "";
  const params = new URLSearchParams(window.location.search);

  if (pageId) params.set("pageId", pageId);
  if (!params.has("page")) params.set("page", String(queryData?.page || 1));
  if (!params.has("perPage")) {
    params.set("perPage", String(queryData?.perPage || 10));
  }
  if (!params.has("sortBy")) {
    params.set("sortBy", queryData?.sortBy || "relevance");
  }
  if (!params.has("sortOrder")) {
    params.set("sortOrder", queryData?.sortOrder || "asc");
  }

  params.set("loadAllPages", "false");

  return params;
}

async function fetchFilteredSearchResults() {
  try {
    const response = await fetch(
      `/api/filteredsearch?${buildFilteredSearchParams().toString()}`,
    );

    if (!response.ok) {
      return { results: [], totalResults: 0 };
    }

    const json = await response.json();
    return {
      results: json?.results || [],
      totalResults: json?.totalResults || 0,
    };
  } catch {
    return { results: [], totalResults: 0 };
  }
}

async function fetchQueryIndexSheet() {
  const { results, totalResults } = await fetchFilteredSearchResults();

  return {
    totalResults,
    entries: results.map(entry => {
      const rawUrl = entry?.contentUrl || "";
      let normalizedPath = rawUrl;

      try {
        const parsedUrl = new URL(rawUrl, window.location.origin);
        normalizedPath = `${parsedUrl.pathname}${parsedUrl.search}`;
      } catch {
        normalizedPath = rawUrl;
      }

      const publishedAt = entry?.date ? new Date(entry.date).getTime() : 0;
      const modifiedAt = entry?.date
        ? new Date(entry.date).getTime()
        : publishedAt;

      return {
        title: entry?.title || "",
        path: normalizedPath,
        image: entry?.imageSrc || "",
        description: entry?.body
          ? cleanText(entry.body.replace(/<[^>]+>/g, ""))
          : "",
        author: Array.isArray(entry?.authors) ? entry.authors.join(", ") : "",
        date: publishedAt ? Math.floor(publishedAt / 1000) : 0,
        lastModified: modifiedAt ? Math.floor(modifiedAt / 1000) : 0,
        tags: entry?.tags || [],
      };
    }),
  };
}

function ensureLegacyAemUrlChangeWatcher() {
  if (window.__legacyAemUrlChangeWatcherInitialized) return;

  window.__legacyAemUrlChangeWatcherInitialized = true;

  const emitUrlChange = () =>
    window.dispatchEvent(new Event("legacy-aem-url-change"));
  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function pushState(...args) {
    originalPushState.apply(this, args);
    emitUrlChange();
  };

  window.history.replaceState = function replaceState(...args) {
    originalReplaceState.apply(this, args);
    emitUrlChange();
  };

  window.addEventListener("popstate", emitUrlChange);
}

function observeLegacyAemUrlChanges(handlerKey, callback) {
  ensureLegacyAemUrlChangeWatcher();

  if (window[handlerKey]) return;

  window[handlerKey] = () => {
    callback();
  };

  window.addEventListener("legacy-aem-url-change", window[handlerKey]);
}

function upsertSchemaScript(scriptId, schemaInfo) {
  if (!schemaInfo) return;

  let script = document.getElementById(scriptId);

  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = scriptId;
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(schemaInfo, null, 2);
}

export function createVideoSchema(embedId, description, uploadDate) {
  return {
    "@type": "VideoObject",
    name: "Content from YouTube",
    description,
    thumbnailUrl: `https://img.youtube.com/vi/${embedId}/hqdefault.jpg`,
    embedUrl: `https://www.youtube.com/embed/${embedId}?playlist=${embedId}&origin=https://www.stewart.com&autoplay=0&loop=0&rel=0&enablejsapi=1`,
    contentUrl: `https://www.youtube.com/watch?v=${embedId}`,
    uploadDate,
  };
}

export function createVimeoVideoSchema(
  embedId,
  description,
  thumbnailUrl,
  uploadDate = "2025-05-30",
  name,
) {
  const url = `https://player.vimeo.com/video/${embedId}`;

  return {
    "@type": "VideoObject",
    name: name || "Content from Vimeo",
    description: description || "",
    thumbnailUrl: thumbnailUrl || "",
    embedUrl: url,
    contentUrl: url,
    uploadDate,
  };
}

export function buildItemList(
  headingSelector,
  sectionName,
  sectionDescription,
  itemSelector = ".teaser-list-item",
  linkSelector = "a",
) {
  const headingElement = document.querySelector(headingSelector);
  if (!headingElement) return null;

  const sectionElement = headingElement.closest(".section");

  const container =
    sectionElement?.querySelector(".button-list.block") ||
    headingElement.parentElement?.nextElementSibling;

  if (!container) return null;

  const items = Array.from(container.querySelectorAll(itemSelector));
  if (!items.length) return null;

  const isTeaserList = items.some(item =>
    item.classList.contains("teaser-list-item"),
  );

  const itemListElements = items.map((item, index) => {
    if (isTeaserList) {
      const subhead =
        item.querySelector(".teaser-list-item-subhead")?.textContent?.trim() ||
        "";

      const linkAnchor = item.querySelector(".teaser-list-item-title a[href]");
      const name = linkAnchor ? linkAnchor.textContent.trim() : "";

      const url = linkAnchor
        ? new URL(linkAnchor.getAttribute("href") || "", window.location.origin)
            .href
        : "";

      return {
        "@type": "ListItem",
        position: index + 1,
        name,
        url,
        description: subhead,
      };
    }

    const link = item.querySelector(`${linkSelector}[href]`);

    return {
      "@type": "ListItem",
      position: index + 1,
      name: link ? link.textContent.trim() : "",
      url: link
        ? new URL(link.getAttribute("href") || "", window.location.origin).href
        : "",
      description: "",
    };
  });

  return {
    "@type": "ItemList",
    name: sectionName,
    description: sectionDescription,
    itemListElement: itemListElements,
  };
}

export function generatePeopleSchema() {
  const people = [];

  const isAgencyServicesContacts = window.location.href.endsWith(
    "/en/customer-type/title-agents/contact-us/agency-services-contacts",
  );

  const cards = isAgencyServicesContacts
    ? document.querySelectorAll(".card.light")
    : document.querySelectorAll(
        ".person-detail-card, .people-listing__card, .card.light",
      );

  cards.forEach(card => {
    let name = "";
    let email = "";
    let image = "";
    let jobTitle = "";

    if (isAgencyServicesContacts) {
      name =
        card
          .querySelector(".card__details h3, .card__details h4")
          ?.textContent?.trim() || "";

      jobTitle =
        card.querySelector(".card__details p strong")?.textContent?.trim() ||
        "";

      email =
        card
          .querySelector("a[href^='mailto:']")
          ?.getAttribute("href")
          ?.replace("mailto:", "")
          .trim() || "";

      image = card.querySelector("img")?.getAttribute("src") || "";
    } else {
      name =
        card
          .querySelector(".display-name, .people-listing__name")
          ?.textContent?.trim() || "";

      email =
        card
          .querySelector(".email a, .people-listing__bio-link[href^='mailto:']")
          ?.getAttribute("href")
          ?.replace("mailto:", "")
          .trim() || "";

      image =
        card
          .querySelector("img.people-listing__image, img")
          ?.getAttribute("src") || "";

      const listingPositions = Array.from(
        card.querySelectorAll(".people-listing__position p"),
      )
        .map(el => el.textContent?.trim() || "")
        .filter(Boolean);

      if (listingPositions.length) {
        jobTitle = listingPositions.join(", ");
      } else {
        jobTitle = Array.from(card.querySelectorAll(".job-title h4"))
          .map(el => el.textContent?.trim() || "")
          .filter(Boolean)
          .join(", ");
      }
    }

    if (name) {
      const person = {
        "@type": "Person",
        name,
      };

      if (jobTitle) person.jobTitle = jobTitle;
      if (email) person.email = email;
      if (image) person.image = image;

      people.push(person);
    }
  });

  return people.length ? people : null;
}

export function generateSchemaForFinCen() {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: getMetadata("og:title"),
    url: getMetadata("og:url") || window.location.href,
    description: getMetadata("og:description"),
    image: {
      "@type": "ImageObject",
      url: getMetadata("og:image"),
    },
    provider: {
      "@type": "Organization",
      name: "Stewart Title",
      url: "https://www.stewart.com",
    },
    video: [
      createVideoSchema("zW-k_Glbg4g", "intro: Meet Fin & Cen", "2025-06-20"),
      createVideoSchema(
        "o6oU5TVD8L0",
        "Video 1- What is FinCEN and the New Anti-Money Laundering Rule?",
        "2025-06-20",
      ),
      createVideoSchema(
        "lPvE559cBv4",
        "Video 2- How does the FinCEN Anti-Money Laundering Rule Differ from Geographic Targeting Orders?",
        "2025-06-20",
      ),
      createVideoSchema(
        "hjFYn-NO3V0",
        "Video 3- What Qualifies as a Residential Transfer of Real Property?",
        "2025-06-20",
      ),
      createVideoSchema(
        "9Bp8rZaHvGw",
        "Video 4- What Qualifies as a Legal Entity or a Trust?",
        "2025-06-20",
      ),
      createVideoSchema(
        "8sKF7icAZxE",
        "Video 5- What Qualifies as a Non-Financed Transfer?",
        "2025-06-20",
      ),
      createVideoSchema(
        "aYFfsuVv4bQ",
        "Video 6 - What Are FinCEM Anti-Money Laundering Rule Reporting Requirements?",
        "2025-08-12",
      ),
      createVideoSchema(
        "04lWsXi-BEM",
        "Video 7 - Who is Responsible for Reporting?",
        "2025-08-25",
      ),
      createVideoSchema(
        "P-sQuFgqUDY",
        "Video 8 - What are Exceptions to the FinCEN Anti-Money Laundering Rule?",
        "2025-08-12",
      ),
      createVideoSchema(
        "YaGcwOo2Nto",
        "Video 9 - What Are the Penalties for Not Complying with the Rule?",
        "2025-08-12",
      ),
    ],
  };
}

export function generateSchemaForMortgageLenders() {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: getMetadata("og:title"),
    url: getMetadata("og:url") || window.location.href,
    description: getMetadata("og:description"),
    image: {
      "@type": "ImageObject",
      url: getMetadata("og:image"),
    },
    provider: {
      "@type": "Organization",
      name: "Stewart Title",
      url: "https://www.stewart.com",
    },
    video: [
      createVideoSchema(
        "uU4FHb-9iBQ",
        "Stewart Lender Services Overview",
        "2022-10-21",
      ),
      createVideoSchema(
        "HzfHK3VHbgs",
        "Stewart In The Studio E01 eMortgage Transformation The 2024 Done For You Approach",
        "2024-08-24",
      ),
      createVideoSchema(
        "Wt8Q8ptKKwE",
        "Stewart In The Studio E02 Credit Score and Reporting Changes Update from MISMO Summit",
        "2024-08-24",
      ),
      createVideoSchema(
        "SW4snEzaIbA",
        "Stewart In The Studio E03 Appraisal Modernization and Diversity for Improved Quality and Turn time",
        "2024-08-24",
      ),
      createVideoSchema(
        "eNf62OGcYH0",
        "Stewart In The Studio E04 Computer Vision Revolutionizing the Appraisal Industry",
        "2024-08-24",
      ),
      createVideoSchema(
        "OIObjhcAXD8",
        "Stewart In The Studio E05 Stewart From Mortgage Data to Mortgage Servicing",
        "2024-08-24",
      ),
      createVideoSchema(
        "rM8woTFSWFQ",
        "Stewart In The Studio E06 Driving Efficiency and Savings in Default Servicing",
        "2024-08-24",
      ),
      createVideoSchema(
        "3ERkDsOYn9s",
        "Stewart In The Studio E07 How to Improve Your Title Technology Experience",
        "2024-08-24",
      ),
      createVideoSchema(
        "vN1VnaOEPrc",
        "Stewart In the Studio E08 Validate Income Employment and Assets Using A Single VOA Report",
        "2024-08-24",
      ),
      createVideoSchema(
        "K9UHW6dNJiA",
        "Stewart In The Studio E09 Home Equity Reduce Costs and Speed Time To Close with Proven Strategie",
        "2024-08-24",
      ),
      createVideoSchema(
        "Gw8V6ugPiw8",
        "Stewart In The Studio E10 The Value of Title Insurance What Lenders Need to Know",
        "2024-08-24",
      ),
      createVideoSchema(
        "MXsZbe-t7gA",
        "Stewart In The Studio E11 Risks and Unknowns Behind Title Alternatives",
        "2024-08-24",
      ),
      createVideoSchema(
        "GikxbQRfio0",
        "Stewart In The Studio E12 Computer Vision: Revolutionizing the Appraisal Industry",
        "2024-09-17",
      ),
      createVideoSchema(
        "i269kcUwjAQ",
        "Stewart In The Studio E13 Delivering Solutions for Every Stage of the Full Mortgage Life Cycle",
        "2024-09-17",
      ),
      createVideoSchema(
        "vUYjbGgwZ98",
        "Stewart In The Studio E14 A Forward Look at Reverse Mortgages",
        "2024-09-25",
      ),
      createVideoSchema(
        "RUJPSuVHgKY",
        "Stewart in the Studio E15 eMortgage Transformation Simplified",
        "2025-01-30",
      ),
      createVideoSchema(
        "2q6zsW1mneo",
        "Stewart in The Studio E16 How Data is Transforming Mortgage Servicing Climate Risk Strategies",
        "2025-01-30",
      ),
    ],
  };
}

export function generateWebPageAndServicesSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: getMetadata("og:title") || document.title || "",
    description:
      getMetadata("og:description") || getMetadata("description") || "",
    url: getMetadata("og:url") || window.location.href,
    image: {
      "@type": "ImageObject",
      url: getMetadata("og:image") || "",
    },
    provider: {
      "@type": "Organization",
      name: "Stewart Title",
      url: "https://www.stewart.com",
    },
  };

  if (window.location.href.endsWith("/en/pk/services")) {
    const mainContent = document.querySelector("main");
    if (!mainContent) return schema;

    const candidateSections = Array.from(
      mainContent.querySelectorAll(
        "section.rich-text.js-rich-text, section.rich-text, section.js-rich-text",
      ),
    ).filter(section => section.querySelector("h3"));

    const scopes = candidateSections.length ? candidateSections : [mainContent];

    schema.hasPart = [];

    scopes.forEach(scope => {
      const headings = Array.from(scope.querySelectorAll("h3")).filter(
        h => !h.closest(".columns, .column"),
      );

      headings.forEach(heading => {
        const serviceSchema = {
          "@type": "Service",
          name: cleanText(heading.textContent) || "Unnamed Section",
          description: "No description available",
          url: "#",
        };

        let firstParagraph = "";
        let listDescription = "";
        let resolvedUrl = "";
        let nextElement = heading.nextElementSibling;

        while (nextElement && nextElement.tagName !== "H3") {
          if (!firstParagraph && nextElement.tagName === "P") {
            firstParagraph = cleanText(nextElement.textContent);
          }

          if (!listDescription && nextElement.tagName === "UL") {
            const listItems = Array.from(nextElement.querySelectorAll("li"))
              .map((li, index) => `${index + 1}. ${cleanText(li.textContent)}`)
              .filter(Boolean);

            listDescription = listItems.join(" ");
          }

          if (!resolvedUrl) {
            const linkElement = nextElement.querySelector("a[href]");
            if (linkElement) {
              const href = linkElement.getAttribute("href") || "#";
              resolvedUrl = href.startsWith("/")
                ? `${window.location.origin}${href}`
                : href;
            }
          }

          nextElement = nextElement.nextElementSibling;
        }

        serviceSchema.description =
          firstParagraph || listDescription || "No description available";
        serviceSchema.url = resolvedUrl || "#";

        schema.hasPart.push({
          "@type": "WebPageElement",
          mainEntity: serviceSchema,
        });
      });
    });
  } else {
    const main = document.querySelector("main");
    if (!main) return schema;

    const isSection = el => el.querySelector("h2, h3");
    const isToolsResourcesPage = window.location.pathname.includes(
      "/tools-and-resources",
    );
    const isCentralTxAccountabilityRatingsPage =
      window.location.pathname.includes(
        "/central-texas-accountability-ratings",
      );
    const isWebinarsAndWhitepapersPage = window.location.pathname.includes(
      "/webinars-and-white-papers",
    );
    const isResourcesPage = window.location.pathname.includes("/resources");
    const isOrderCenterServicesPage =
      window.location.pathname.endsWith("/order-center");
    const isSouthCarolinaAgentsPage =
      window.location.pathname.replace(/\/$/, "") ===
      "/en/state-pages/south-carolina-agents";
    const isRealEstateClosingServicesPage = window.location.pathname
      .replace(/\/$/, "")
      .endsWith("/real-estate-closing-title-services");
    const isRelocationTitleInsuranceServicesPage =
      window.location.pathname.includes("/relocation-title-insurance-services");
    const isTitleInsuranceClosingServicesPage =
      window.location.pathname.includes("/title-insurance-closing-services");
    const isInternationalTitleServicesPage = window.location.pathname.includes(
      "/international-title-services",
    );
    const isUnderwritingServicesPage =
      window.location.pathname.endsWith("underwriting");
    const isWhatWeOfferPage =
      window.location.pathname.endsWith("/what-we-offer");
    const isSolutionsServicesPage =
      window.location.pathname.endsWith("/solutions");
    const isSearchOrderServicesPage =
      window.location.pathname.endsWith("/search-order");
    const isTitleAndClosingServicesPage = window.location.pathname.endsWith(
      "/title-and-closing-services",
    );
    const isServicesPage = window.location.pathname.endsWith("/services");
    const isCustomerTypeTitleAgentsOtherServicesPage =
      window.location.pathname.includes(
        "/en/customer-type/title-agents/other-services",
      );
    const isAgenSolutionsPage =
      window.location.pathname.includes("/agent-solutions");
    const isDigitalSolutionsPage =
      window.location.pathname.includes("/digital-solutions");
    const isMortgageOriginationsPage = window.location.pathname.includes(
      "/mortgage-originations",
    );
    const isProcessingAndUnderwritingPage = window.location.pathname.includes(
      "/processing-and-underwriting",
    );
    const isClosingJourneyServicesPage =
      window.location.pathname.includes("/closing-journey");
    const isMarketingSourcesPage = window.location.pathname.includes(
      "/marketing-resources",
    );
    const isTechnologyCenterStewartPakistanPage =
      window.location.pathname.includes(
        "/technology-center-of-stewart-pakistan",
      );
    const isCustomerTypeMortgageLendersPage = window.location.pathname.includes(
      "/en/customer-type/mortgage-lenders/other-services",
    );

    const isOtherServicesForAgentsPage = window.location.pathname
      .replace(/\/$/, "")
      .endsWith("/other-services-for-real-estate-agents-and-brokers");
    const shouldUseNestedServiceSections =
      isToolsResourcesPage ||
      isCentralTxAccountabilityRatingsPage ||
      isWebinarsAndWhitepapersPage ||
      isResourcesPage ||
      isRealEstateClosingServicesPage ||
      isRelocationTitleInsuranceServicesPage ||
      isTitleInsuranceClosingServicesPage ||
      isInternationalTitleServicesPage ||
      isWhatWeOfferPage ||
      isUnderwritingServicesPage ||
      isSolutionsServicesPage ||
      isOrderCenterServicesPage ||
      isSearchOrderServicesPage ||
      isTitleAndClosingServicesPage ||
      isServicesPage ||
      isAgenSolutionsPage ||
      isDigitalSolutionsPage ||
      isMortgageOriginationsPage ||
      isProcessingAndUnderwritingPage ||
      isClosingJourneyServicesPage ||
      isMarketingSourcesPage ||
      isTechnologyCenterStewartPakistanPage ||
      isCustomerTypeMortgageLendersPage ||
      isCustomerTypeTitleAgentsOtherServicesPage;

    const accordionItems = main.querySelectorAll(".accordion-item");
    const nestedSections = main.querySelectorAll("section");

    let sections = shouldUseNestedServiceSections
      ? Array.from(nestedSections).filter(isSection)
      : accordionItems.length > 0
        ? Array.from(accordionItems).filter(isSection)
        : Array.from(main.children).filter(isSection);

    if (isOtherServicesForAgentsPage) {
      sections = Array.from(
        main.querySelectorAll(
          "section.rich-text.js-rich-text, section.rich-text, section.js-rich-text",
        ),
      ).filter(section => Boolean(section.querySelector("h1, h2, h3")));
    }

    if (isSouthCarolinaAgentsPage) {
      const mainContentWrapper =
        main.querySelector(":scope > .main-content-wrapper") ||
        main.querySelector(".main-content-wrapper") ||
        main;

      const allowedContainers = Array.from(
        mainContentWrapper.querySelectorAll("section.container"),
      ).filter(
        section =>
          (section.classList.contains("container--100") ||
            section.classList.contains("container--bleed")) &&
          !section.classList.contains("container--50-50"),
      );

      sections = allowedContainers.flatMap(container => {
        const contentSections = Array.from(
          container.querySelectorAll("section.rich-text, section.cta-banner"),
        ).filter(isSection);

        if (contentSections.length) return contentSections;

        return Array.from(container.querySelectorAll("section")).filter(
          isSection,
        );
      });
    }

    schema.hasPart = [];
    const seenHasPartKeys = new Set();

    const normalizeUrlForKey = value => {
      if (!value || value === "#") return "#";

      try {
        const parsed = new URL(value, window.location.origin);
        return `${parsed.origin}${parsed.pathname.replace(/\/$/, "")}${parsed.search}`;
      } catch {
        return cleanText(String(value));
      }
    };

    const buildServiceSchemasFromSection = section => {
      const nodes = Array.from(section.querySelectorAll("h2, p, a[href]"));
      const services = [];
      let currentService = null;

      const resolveServiceUrl = href => {
        if (!href) return "";

        const normalizedHref = href.trim();
        if (
          !normalizedHref ||
          normalizedHref.startsWith("#")
        ) {
          return "";
        }

        return normalizedHref.startsWith("/")
          ? `${window.location.origin}${normalizedHref}`
          : normalizedHref;
      };

      const pushCurrentService = () => {
        if (!currentService) return;

        services.push({
          "@type": "Service",
          name: currentService.name || "Unnamed Section",
          description: currentService.description || "No description available",
          url: currentService.url || "#",
        });
      };

      nodes.forEach(node => {
        if (node.matches("h2, h3")) {
          pushCurrentService();
          currentService = {
            name: cleanText(node.textContent),
            description: "",
            url: "",
          };
          return;
        }

        if (!currentService) return;

        if (!currentService.description && node.tagName === "P") {
          currentService.description = cleanText(node.textContent);
        }

        if (!currentService.url && node.tagName === "A") {
          currentService.url = resolveServiceUrl(
            node.getAttribute("href") || "",
          );
        }
      });

      pushCurrentService();

      if (services.length > 1) {
        return services;
      }

      const heading =
        section.querySelector("h2") || section.querySelector("h3");
      const firstParagraph = section.querySelector("p");
      const firstLink = section.querySelector("a[href]");
      const href = resolveServiceUrl(firstLink?.getAttribute("href") || "");

      return [
        {
          "@type": "Service",
          name: heading ? cleanText(heading.textContent) : "Unnamed Section",
          description: firstParagraph
            ? cleanText(firstParagraph.textContent)
            : "No description available",
          url: href.startsWith("/") ? `${window.location.origin}${href}` : href,
        },
      ];
    };

    sections.forEach(section => {
      const serviceSchemas =
        isRealEstateClosingServicesPage ||
        isRelocationTitleInsuranceServicesPage ||
        isTitleInsuranceClosingServicesPage ||
        isInternationalTitleServicesPage ||
        isWhatWeOfferPage ||
        isUnderwritingServicesPage ||
        isSolutionsServicesPage ||
        isOrderCenterServicesPage ||
        isSearchOrderServicesPage ||
        isTitleAndClosingServicesPage ||
        isServicesPage ||
        isCustomerTypeTitleAgentsOtherServicesPage
          ? buildServiceSchemasFromSection(section)
          : [
              (() => {
                const heading =
                  section.querySelector("h1") ||
                  section.querySelector("h2") ||
                  section.querySelector("h3");
                const paragraphs = Array.from(section.querySelectorAll("p"))
                  .filter(
                    paragraph =>
                      !paragraph.classList.contains("button-container"),
                  )
                  .map(paragraph => cleanText(paragraph.textContent))
                  .filter(Boolean);
                const link = section.querySelector("a");

                const href = link?.getAttribute("href") || "#";

                return {
                  "@type": "Service",
                  name: heading
                    ? heading.textContent.trim()
                    : "Unnamed Section",
                  description: isOtherServicesForAgentsPage
                    ? paragraphs[0] || "No description available"
                    : paragraphs.length
                      ? paragraphs.join(" ")
                      : "No description available",
                  url: href.startsWith("/")
                    ? `${window.location.origin}${href}`
                    : href,
                };
              })(),
            ];

      serviceSchemas.forEach(serviceSchema => {
        // Special case
        if (
          window.location.href.endsWith("tools-and-resources") &&
          section.matches("div.section.columns-container") &&
          section.classList.length === 2 &&
          !window.location.href.includes("customer-type")
        ) {
          serviceSchema.subjectOf = createVideoSchema(
            "-SEiAVIDiaw",
            "Use our Stewart Now™ mobile app and online portal to advance your business and provide a better customer experience. From tracking the status of your order, to farming and property profiles, Stewart Now is the time-saving tool you need.",
            "2020-08-30",
          );
        }

        const hasPartKey = cleanText(serviceSchema.name).toLowerCase();

        if (seenHasPartKeys.has(hasPartKey)) return;
        seenHasPartKeys.add(hasPartKey);

        schema.hasPart.push({
          "@type": "WebPageElement",
          mainEntity: serviceSchema,
        });
      });
    });
  }

  if (window.location.href.endsWith("/en/state-pages/south-carolina-agents")) {
    schema.video = [
      createVideoSchema(
        "IzDoHTBwwy4",
        "Protect Your Real Estate Transactions Against Wire Fraud in 3 Easy Steps",
        "2025-05-09",
      ),
      createVideoSchema(
        "KKrXa4N7lF0",
        "Stewart Journey to Be the Premier Partner for Independent Title Agents",
        "2023-08-18",
      ),
    ];
  }

  return schema;
}

export function createPersonSchemaFromElement(element, company) {
  const personSchema = {
    "@type": "Person",
    name: "",
    jobTitle: "",
    affiliation: company,
    email: "",
  };

  if (window.location.href.includes("commercial-escrow")) {
    const nameElement = element.childNodes[1];
    if (nameElement) {
      personSchema.name = nameElement.textContent.trim();
    }

    const jobTitleElement = element.childNodes[3];
    if (jobTitleElement) {
      personSchema.jobTitle = jobTitleElement.textContent.trim();
    }
  } else {
    const nameElement = element.childNodes[0];
    if (nameElement) {
      personSchema.name = nameElement.textContent.trim();
    }

    const jobTitleElement = element.childNodes[2];
    if (jobTitleElement) {
      personSchema.jobTitle = jobTitleElement.textContent.trim();
    }
  }

  const emailElement = element.querySelector('a[href^="mailto:"]');
  if (emailElement) {
    personSchema.email = emailElement.textContent.trim();
  }

  return personSchema;
}

export function generateSchemaForLandingPage() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: getMetadata("og:title") || "Default Title",
    description: getMetadata("og:description") || "Default Description",
    url: getMetadata("og:url") || window.location.href,
    image: {
      "@type": "ImageObject",
      url: getMetadata("og:image") || "",
    },
    provider: {
      "@type": "Organization",
      name: "Stewart Title",
      url: "https://www.stewart.com",
    },
  };

  let mainContent = document.querySelectorAll(".default-content-wrapper")[1];
  if (!mainContent) return schema;

  schema.hasPart = [];

  let headings = mainContent.querySelectorAll("h2, h3, h4, h5, h6");

  if (headings.length === 0) {
    mainContent = document.querySelector("main");
    if (!mainContent) return schema;

    headings = mainContent.querySelectorAll("h2, h3, h4, h5, h6");
  }

  headings.forEach(heading => {
    const sectionSchema = {
      "@type": "WebPageElement",
      name: heading.textContent.trim() || "Unnamed Section",
      description: "No description available",
      url: "#",
    };

    let descriptionText = "";
    let nextElement = heading.nextElementSibling;

    while (nextElement && nextElement.tagName !== heading.tagName) {
      if (nextElement.tagName === "P") {
        descriptionText += `${nextElement.textContent.trim()} `;

        const linkElement = nextElement.querySelector("a");
        if (linkElement) {
          const href = linkElement.getAttribute("href") || "#";
          sectionSchema.url = href.startsWith("/")
            ? `${window.location.origin}${href}`
            : href;
        }
      }

      if (nextElement.tagName === "UL") {
        const listItems = nextElement.querySelectorAll("li");
        for (let i = 0; i < listItems.length; i += 1) {
          descriptionText += `${i + 1}. ${listItems[i].textContent.trim()} `;
        }
      }

      nextElement = nextElement.nextElementSibling;
    }

    sectionSchema.description =
      descriptionText.trim() || "No description available";

    schema.hasPart.push(sectionSchema);
  });

  schema.mainEntity = [];

  const columns = document.querySelectorAll(".column");
  columns.forEach(column => {
    const personSchema = createPersonSchemaFromElement(
      column,
      "Stewart Title and Escrow, Inc..",
    );

    if (personSchema.name && personSchema.email) {
      schema.mainEntity.push(personSchema);
    }
  });

  if (!schema.mainEntity.length) {
    delete schema.mainEntity;
  }

  const url = window.location.href;

  if (url.endsWith("customer360")) {
    schema.subjectOf = createVideoSchema(
      "-RNN1zI3duI",
      "Customer 360 | Stewart Title - The Trusted Partner for Real Estate Services",
      "2020-08-31",
    );
  } else if (url.endsWith("stc-national-ron")) {
    schema.subjectOf = createVideoSchema(
      "PeWd7kdJr8g",
      "Streamlining Customer Experiences with Remote Online Notarization RON",
      "2020-08-31",
    );
  }

  return schema;
}

export function generateAccordionItemListSchema(description) {
  const currentUrl = window.location.href.replace(/\/$/, "");
  const accordionItems = document.querySelectorAll(".accordion-item");
  if (!accordionItems.length) return null;

  const itemListElement = [];

  accordionItems.forEach((item, idx) => {
    const heading = item.querySelector(
      "h1, h2, h3, h4, h5, h6, .accordion-item__title",
    );
    const title = heading ? heading.textContent.trim() : "Default Title";

    let listItem = {
      "@type": "ListItem",
      position: idx + 1,
      name: title,
    };

    if (window.location.href.endsWith("more-than-title")) {
      const contentRoot =
        item.querySelector(".js-accordion-content") ||
        item.querySelector(".accordion-wrapper") ||
        item;

      const paragraphTexts = Array.from(
        contentRoot.querySelectorAll(".rich-text p, p"),
      )
        .map(p => cleanText(p.textContent || ""))
        .filter(Boolean);

      const contentDescription = paragraphTexts.join(" ");
      const desc = contentDescription;

      const link = contentRoot.querySelector("a[href]");
      let href = link ? link.getAttribute("href") : "#";

      if (href.startsWith("/")) {
        href = `${window.location.origin}${href}`;
      }

      listItem.description = desc;
      listItem.url = href;
    } else if (
      currentUrl.endsWith("/en/customer-type/mortgage-lenders/servicing")
    ) {
      const panel =
        item.querySelector(".accordion-panel") ||
        item.querySelector(".js-accordion-content") ||
        item.querySelector(".accordion-item__content") ||
        item.querySelector(".accordion-wrapper");
      const descriptionParts = [];

      if (panel) {
        const textNodes = Array.from(
          panel.querySelectorAll(".rich-text p, .rich-text span, p, span"),
        )
          .map(node => cleanText(node.textContent || ""))
          .filter(Boolean);

        descriptionParts.push(...textNodes);

        Array.from(panel.querySelectorAll("ul, ol")).forEach(list => {
          Array.from(list.querySelectorAll("li")).forEach((li, i) => {
            const liText = cleanText(li.textContent || "");
            if (liText) {
              descriptionParts.push(`${i + 1}. ${liText}.`);
            }
          });
        });
      }

      listItem.description =
        descriptionParts.length > 0
          ? descriptionParts.join(" ")
          : "No description available";
    } else {
      const paragraphs = Array.from(item.querySelectorAll("p"));

      const desc = paragraphs
        .map(p => p.textContent.trim())
        .filter(Boolean)
        .join(". ");

      const link = item.querySelector("a[href]");
      let href = link ? link.getAttribute("href") : "#";

      if (href.startsWith("/")) {
        href = `${window.location.origin}${href}`;
      }

      listItem.description = desc;
      listItem.url = href;
    }

    itemListElement.push(listItem);
  });

  if (!itemListElement.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: description,
    itemListElement,
  };
}

export function generateBasicWebPageSchema() {
  const regularParagraphs = document.querySelectorAll(
    ".default-content-wrapper p:not(.button-container)",
  );

  const mergedText = Array.from(regularParagraphs)
    .map(p => p.textContent.trim())
    .join(" ");

  const richTextBlocks = document.querySelectorAll(
    ".default-content-wrapper .rich-text, .default-content-wrapper section.rich-text, .default-content-wrapper .js-rich-text, main .rich-text, main section.rich-text, main .js-rich-text",
  );

  const richTextContent = Array.from(richTextBlocks)
    .map(block => cleanText(block.textContent || ""))
    .filter(Boolean)
    .join(" ");

  const isQuickLinksPage = window.location.href.endsWith("quick-links");
  const isCultureOfCaringPage =
    window.location.href.endsWith("culture-of-caring");

  let descriptionParts = isQuickLinksPage
    ? [getMetadata("og:description"), mergedText, richTextContent]
    : [getMetadata("og:description")];

  if (isCultureOfCaringPage) {
    const richTextSections = document.querySelectorAll(
      ".container--100:not(.has-background) .rich-text p, .container--100:not(.has-background) .js-rich-text p",
    );
    const richTextParagraphs = Array.from(richTextSections)
      .filter(p => !p.querySelector("a") && !p.querySelector("strong"))
      .map(p => cleanText(p.textContent || ""))
      .filter(Boolean);
    descriptionParts.push(...richTextParagraphs);
  }

  const uniqueDescriptionParts = [...new Set(descriptionParts)];

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: getMetadata("og:title") || document.title || "Default Title",
    url: getMetadata("og:url") || window.location.href,
    description: uniqueDescriptionParts.join(" "),
    image: {
      "@type": "ImageObject",
      url: getMetadata("og:image") || "",
    },
    provider: {
      "@type": "Organization",
      name: "Stewart Title",
      url: "https://www.stewart.com",
    },
  };

  if (window.location.href.endsWith("pay-practice-change")) {
    schema.subjectOf = createVideoSchema(
      "rxS4BZ7X9tU",
      "Stewart: Pay Practice Change Video.",
      "2020-10-21",
    );
  }

  if (isQuickLinksPage) {
    schema.mainEntity = {
      "@type": "ItemList",
      name: "Quick Links",
      itemListElement: [],
    };

    const mainContentWrapper =
      document.querySelector(".main-content-wrapper") ||
      document.querySelector(".main-content-wapper");
    const links = Array.from(
      mainContentWrapper?.querySelectorAll("a[href]") || [],
    ).filter(link => {
      const label = cleanText(link.textContent);
      const rawHref = (link.getAttribute("href") || "").trim();
      return (
        label &&
        rawHref &&
        !rawHref.startsWith("#") &&
        !rawHref.toLowerCase().startsWith("javascript:")
      );
    });

    links.forEach((link, index) => {
      schema.mainEntity.itemListElement.push({
        "@type": "ListItem",
        position: index + 1,
        name: cleanText(link.textContent),
        url: link.href,
      });
    });
  }

  return schema;
}

export function populateListItems(schemaInfo) {
  let featureItems = document.querySelectorAll(".list-item.with-icon");

  if (featureItems.length === 0) {
    featureItems = document.querySelectorAll("li.list-item");
  }

  if (featureItems.length === 0) {
    featureItems = document.querySelectorAll(".listing__list-item");
  }

  featureItems.forEach(li => {
    let heading =
      li.querySelector("h4")?.textContent.trim() ||
      li.querySelector(".listing__list-text")?.textContent.trim() ||
      "";

    if (!heading) {
      heading = li.querySelector("h3")?.textContent.trim() || "#";
    }

    let text =
      li.querySelector("p")?.textContent.trim() ||
      li.querySelector(".listing__list-subtext")?.textContent.trim() ||
      "";

    if (!text) {
      const nestedListItem = li.querySelector("li.list-item");

      if (nestedListItem) {
        text =
          nestedListItem.querySelectorAll("p")[1]?.textContent.trim() || "";
      } else {
        const h4 = li.querySelector("h4");

        if (
          h4 &&
          h4.nextElementSibling &&
          h4.nextElementSibling.tagName === "UL"
        ) {
          text = "";
          const listItems = h4.nextElementSibling.querySelectorAll("li");

          for (let i = 0; i < listItems.length; i += 1) {
            text += `${i + 1}. ${listItems[i].textContent.trim()} `;
          }
        }
      }
    }

    if (heading) {
      schemaInfo.mainEntity.itemListElement.push({
        "@type": "ListItem",
        position: schemaInfo.mainEntity.itemListElement.length + 1,
        name: heading === "#" ? text || "" : heading,
        description: text || "",
      });
    }
  });

  if (schemaInfo.mainEntity.itemListElement.length === 0) {
    delete schemaInfo.mainEntity;
  }

  return schemaInfo;
}

export function generateFAQSchema() {
  const url = window.location.href;
  const accordionContainer =
    document.querySelector(".accordion-items") ||
    document.querySelector(".accordion__container") ||
    document.querySelector(".accordion-container") ||
    document.querySelector(".accordion");
  const hasAccordionFaq =
    Boolean(accordionContainer) &&
    accordionContainer.querySelectorAll(".accordion-item").length > 0;

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    name: getMetadata("og:title") || "",
    url: getMetadata("og:url") || window.location.href,
    description: getMetadata("og:description") || "",
    image: {
      "@type": "ImageObject",
      url: getMetadata("og:image") || "",
    },
    provider: {
      "@type": "Organization",
      name: "Stewart Title",
      url: "https://www.stewart.com",
    },
  };

  const buildQuestionSchema = (questionText, answerText, url) => ({
    "@type": "Question",
    name: questionText,
    text: questionText,
    acceptedAnswer: {
      "@type": "Answer",
      text: cleanText(answerText),
      ...(url ? { url } : {}),
    },
  });

  let mainContent = document.querySelectorAll(".default-content-wrapper")[1];

  if (
    (!mainContent || url.endsWith("/en/fincen-aml/faqs")) &&
    hasAccordionFaq
  ) {
    mainContent = accordionContainer;
  }

  if (!mainContent) {
    mainContent = document.querySelector("main");
  }

  schema.mainEntity = [];

  if (url.endsWith("sustainability-q-n-a")) {
    const questions = document.querySelectorAll("h4");

    questions.forEach(question => {
      const questionText = cleanText(question.textContent);
      let answerText = "";
      let nextElement = question.nextElementSibling;

      while (nextElement && nextElement.tagName !== "H4") {
        if (nextElement.tagName === "P") {
          answerText += `${cleanText(nextElement.textContent)} `;
        }

        if (nextElement.tagName === "OL") {
          const listItems = nextElement.querySelectorAll("li");
          for (let i = 0; i < listItems.length; i += 1) {
            answerText += `${i + 1}. ${cleanText(listItems[i].textContent)} `;
          }
        }

        nextElement = nextElement.nextElementSibling;
      }

      schema.mainEntity.push(buildQuestionSchema(questionText, answerText));
    });
  } else if (hasAccordionFaq) {
    const questions = mainContent.querySelectorAll("h2, h3, button");

    questions.forEach(question => {
      const questionText = cleanText(question.textContent);
      let answerText = "";
      let questionUrl = "";

      const panel = question
        .closest(".accordion-item")
        ?.querySelector(
          ".accordion-panel, .accordion-wrapper, .accordion-content, .accordion__content",
        );

      if (panel) {
        if (url.endsWith("/en/fincen-aml/faqs")) {
          Array.from(panel.children).forEach(child => {
            if (child.tagName === "P") {
              answerText += `${cleanText(child.textContent)}. `;
              const anchor = child.querySelector("a[href]");
              if (anchor) {
                questionUrl = anchor.href.trim();
              }
            }

            if (child.tagName === "SPAN") {
              answerText += `${cleanText(child.textContent)}. `;
            }

            if (child.tagName === "UL" || child.tagName === "OL") {
              Array.from(child.children).forEach((li, idx) => {
                answerText += `${idx + 1}. ${cleanText(li.textContent)}. `;
              });
            }
          });

          if (answerText === "") {
            answerText = cleanText(panel.textContent);
          }
        } else {
          const paragraphs = panel.querySelectorAll("p");
          const spans = panel.querySelectorAll("span");
          const listItems = panel.querySelectorAll("ol li, ul li");

          paragraphs.forEach(p => {
            answerText += `${cleanText(p.textContent)} `;
          });

          spans.forEach(span => {
            answerText += `${cleanText(span.textContent)} `;
          });

          listItems.forEach((li, index) => {
            answerText += `${index + 1}. ${cleanText(li.textContent)} `;
          });
        }
      }

      if (questionText && answerText) {
        schema.mainEntity.push(
          buildQuestionSchema(questionText, answerText, questionUrl),
        );
      }
    });
  } else if (mainContent) {
    const questions = mainContent.querySelectorAll("h3, h4");

    questions.forEach(question => {
      const questionText = cleanText(question.textContent);
      let answerText = "";
      let nextElement = question.nextElementSibling;

      while (nextElement && nextElement.tagName !== "H3") {
        if (nextElement.tagName === "P") {
          answerText += `${cleanText(nextElement.textContent)} `;
        }

        if (nextElement.tagName === "SPAN") {
          answerText += `${cleanText(nextElement.textContent)} `;
        }

        if (nextElement.tagName === "OL" || nextElement.tagName === "UL") {
          const listItems = nextElement.querySelectorAll("li");
          for (let i = 0; i < listItems.length; i += 1) {
            answerText += `${i + 1}. ${cleanText(listItems[i].textContent)} `;
          }
        }

        nextElement = nextElement.nextElementSibling;
      }

      if (questionText && answerText) {
        schema.mainEntity.push(buildQuestionSchema(questionText, answerText));
      }
    });
  }

  if (!schema.mainEntity.length) {
    delete schema.mainEntity;
  }

  return schema;
}

function createListItemsFromColumns(columnSelector, title) {
  const column = document.querySelector(columnSelector);
  if (!column) return null;

  const itemList = {
    "@type": "ItemList",
    name: title,
    itemListElement: [],
  };

  const anchorHeading = column.querySelector(".button-container a, a[href]");
  const nameSources = [
    ...Array.from(column.querySelectorAll("p")).filter(
      p => !p.querySelector("a, strong, em, span"),
    ),
    ...Array.from(
      column.querySelectorAll(".rich-text div, section.rich-text div"),
    ).filter(div => !div.querySelector("p")),
  ];

  const disallowedName = value => {
    const normalized = cleanText(value).toLowerCase();
    if (!normalized) return true;
    if (
      anchorHeading &&
      cleanText(anchorHeading.textContent).toLowerCase() === normalized
    )
      return true;

    return (
      normalized === cleanText(title).toLowerCase() ||
      normalized === "board of directors" ||
      normalized === "committees" ||
      normalized === "executive team" ||
      normalized.endsWith(" committee")
    );
  };

  const people = [];
  const seenPeople = new Set();

  nameSources.forEach(item => {
    if (anchorHeading && item.contains(anchorHeading)) return;
    if (item.querySelector("a[href], h1, h2, h3, h4, h5, h6")) return;

    const html = item.innerHTML || "";
    const hasBreaks = /<br\s*\/?\s*>/i.test(html);
    const chunks = hasBreaks ? html.split(/<br\s*\/?\s*>/i) : [html];

    chunks.forEach(chunk => {
      const cleanName = cleanText(
        chunk
          .replace(/<[^>]+>/g, "")
          .replace(/^"+|"+$/g, "")
          .replace(/\s+/g, " "),
      );

      if (disallowedName(cleanName)) return;

      const key = cleanName.toLowerCase();
      if (seenPeople.has(key)) return;
      seenPeople.add(key);

      people.push({
        "@type": "Person",
        name: cleanName,
      });
    });
  });

  itemList.itemListElement = people;
  return people.length ? itemList : null;
}

export function createSectionItemList() {
  const schema = generateBasicWebPageSchema();
  const normalizedUrl = window.location.href.replace(/\/$/, "");

  const mainContent = document.querySelector("main");
  if (!mainContent) return schema;

  const isSection = element => {
    const hasH2 = element.querySelector("h2");
    const hasH3 = element.querySelector("h3");
    const hasParagraph = element.querySelector("p");
    const hasListContent = element.querySelector(
      "li.list-item.with-icon, .listing__list-item, li.list-item",
    );
    const container = element.closest("section.container");
    const hasSiblingListContent = container?.nextElementSibling?.querySelector(
      "li.list-item.with-icon, .listing__list-item, li.list-item",
    );

    return (
      (hasH2 || hasH3) &&
      (hasParagraph ||
        (hasH2 && hasH3) ||
        hasListContent ||
        hasSiblingListContent)
    );
  };

  const richTextSections = mainContent.querySelectorAll(
    ".rich-text.js-rich-text, .rich-text, .dynamic-multi-promo__text",
  );
  const accordionItems = mainContent.querySelectorAll(".accordion-item");
  const sections = richTextSections.length
    ? Array.from(richTextSections).filter(isSection)
    : Array.from(accordionItems).filter(isSection);

  schema.mainEntity = {
    "@type": "ItemList",
    name: "Sections List",
    itemListElement: [],
  };

  const getFirstListDescription = scope =>
    cleanText(
      scope?.querySelector(
        "li.list-item.with-icon p, .listing__list-item .listing__list-subtext, li.list-item p, .list-item p",
      )?.textContent || "",
    );

  const getFirstParagraphDescription = scope =>
    cleanText(
      Array.from(scope?.querySelectorAll("p") || [])
        .map(p => cleanText(p.textContent || ""))
        .find(Boolean) || "",
    );

  sections.forEach(section => {
    const listItem = {
      "@type": "ListItem",
      position: schema.mainEntity.itemListElement.length + 1,
    };

    const headingElement = section.querySelector("h2, h3");
    const linkElement = section.querySelector("a");
    const richTextElement = section.matches(".rich-text")
      ? section
      : section.querySelector(".rich-text");

    const isCareersIntroSection =
      normalizedUrl.endsWith("about-stewart/careers") &&
      headingElement?.tagName === "H2" &&
      Boolean(headingElement.closest("center")) &&
      !linkElement;

    if (isCareersIntroSection) return;

    listItem.name = headingElement
      ? cleanText(headingElement.textContent.trim())
      : "Unnamed Section";

    let paragraphTexts = [];

    if (richTextElement) {
      const paragraphElements = Array.from(
        richTextElement.querySelectorAll("p"),
      );
      paragraphTexts = paragraphElements
        .map(p => cleanText(p.textContent.trim()))
        .filter(Boolean);

      // Exclude column-1 first paragraph from initial paragraphTexts for culture-of-caring to avoid duplication
      if (normalizedUrl.endsWith("culture-of-caring")) {
        const columnOne = document.querySelector(".column-1");
        const firstParagraph = columnOne?.querySelector("p");
        if (firstParagraph) {
          const firstParagraphText = cleanText(firstParagraph.textContent);
          paragraphTexts = paragraphTexts.filter(
            text => text !== firstParagraphText,
          );
        }
      }
    }

    const accordionContent = section.querySelector(".accordion-item__content");
    if (accordionContent) {
      const accordionText = cleanText(accordionContent.textContent.trim());
      if (accordionText) {
        paragraphTexts.push(accordionText);
      }
    }

    if (!paragraphTexts.length && headingElement?.tagName === "H2") {
      const sectionContainer = section.closest("section.container");
      const nextContainer = sectionContainer?.nextElementSibling;
      const firstParagraphDescription =
        getFirstParagraphDescription(section) ||
        getFirstParagraphDescription(sectionContainer) ||
        getFirstParagraphDescription(nextContainer);

      if (firstParagraphDescription) {
        paragraphTexts = [firstParagraphDescription];
      }

      const firstListDescription =
        getFirstListDescription(section) ||
        getFirstListDescription(sectionContainer) ||
        getFirstListDescription(nextContainer);

      if (!paragraphTexts.length && firstListDescription) {
        paragraphTexts = [firstListDescription];
      }

      if (!paragraphTexts.length) {
        const subHeading = section.querySelector("h3");
        if (subHeading) {
          const subHeadingText = cleanText(subHeading.textContent.trim());
          if (subHeadingText) paragraphTexts = [subHeadingText];
        }
      }
    }

    let sectionDescription =
      paragraphTexts.length > 0
        ? paragraphTexts.join(", ")
        : "No description available";

    // Add culture-of-caring specific text from HTML (first paragraph from column-1)
    if (normalizedUrl.endsWith("culture-of-caring")) {
      const columnOne = document.querySelector(".column-1");
      const firstParagraph = columnOne?.querySelector("p");
      if (firstParagraph) {
        const cultureOfCaringText = cleanText(firstParagraph.textContent);
        if (cultureOfCaringText && cultureOfCaringText.length > 50) {
          sectionDescription = `${sectionDescription} ${cultureOfCaringText}`;
        }
      }
    }

    listItem.description = sectionDescription;

    // If no link found inside the section, check sibling columns in the parent container
    const parentCol = section.closest(".container__col");
    const resolvedLink =
      linkElement ||
      (parentCol ? parentCol.parentElement?.querySelector("a[href]") : null) ||
      null;

    listItem.url = resolvedLink ? resolvedLink.getAttribute("href") : "#";
    listItem.url = listItem.url.startsWith("/")
      ? `${window.location.origin}${listItem.url}`
      : listItem.url;

    schema.mainEntity.itemListElement.push(listItem);
  });

  if (
    normalizedUrl.endsWith("culture-of-caring") ||
    normalizedUrl.endsWith("about-stewart/careers")
  ) {
    const existingItems = new Set(
      schema.mainEntity.itemListElement.map(item => {
        const nameKey = cleanText(item.name || "").toLowerCase();
        const urlKey = (item.url || "#").trim();
        return `${nameKey}|${urlKey}`;
      }),
    );

    const cardDetails = Array.from(document.querySelectorAll(".card__details"));

    cardDetails.forEach(card => {
      const headingElement = card.querySelector("h2, h3, h4");
      const name = cleanText(headingElement?.textContent || "");
      if (!name) return;

      const paragraphTexts = Array.from(card.querySelectorAll("p"))
        .map(paragraph => cleanText(paragraph.textContent || ""))
        .filter(Boolean);

      const linkElement = card.querySelector("a[href]");
      let itemUrl = (linkElement?.getAttribute("href") || "#").trim();

      if (itemUrl.startsWith("/")) {
        itemUrl = `${window.location.origin}${itemUrl}`;
      }

      const itemKey = `${name.toLowerCase()}|${itemUrl}`;
      if (existingItems.has(itemKey)) return;
      existingItems.add(itemKey);

      schema.mainEntity.itemListElement.push({
        "@type": "ListItem",
        position: schema.mainEntity.itemListElement.length + 1,
        name,
        description: paragraphTexts.join(" "),
        url: itemUrl || "#",
      });
    });

    if (normalizedUrl.endsWith("culture-of-caring")) {
      const anchoredHeadings = Array.from(
        document.querySelectorAll("main section h2[id], main section h3[id]"),
      );

      anchoredHeadings.forEach(heading => {
        const name = cleanText(heading.textContent || "");
        if (!name) return;

        // Skip anchored subheadings that are clearly descriptive sentences.
        const wordCount = name.split(/\s+/).filter(Boolean).length;
        const isDescriptiveSentence = /[.,;:!?]/.test(name) || wordCount > 12;
        if (heading.tagName === "H3" && isDescriptiveSentence) return;

        const section = heading.closest("section") || heading.parentElement;
        if (!section) return;
        if (section.querySelector(".card__details")) return;

        const containerScope =
          heading.closest("section.container") ||
          heading.closest(".container") ||
          heading.closest(".main-content-wrapper") ||
          section;

        const firstParagraphDescription = cleanText(
          Array.from(section.querySelectorAll("p")).find(
            node => node !== heading && cleanText(node.textContent || ""),
          )?.textContent || "",
        );

        const firstSubHeadingDescription = cleanText(
          Array.from(section.querySelectorAll("h3")).find(
            node => node !== heading && cleanText(node.textContent || ""),
          )?.textContent || "",
        );

        const description =
          firstParagraphDescription ||
          firstSubHeadingDescription ||
          "No description available";

        const linkElement =
          section.querySelector("a[href*='.pdf']") ||
          containerScope.querySelector("a[href*='.pdf']") ||
          section.querySelector("a[href*='report']") ||
          containerScope.querySelector("a[href*='report']") ||
          section.querySelector("a[href]");

        if (!linkElement) return;

        let itemUrl = (linkElement.getAttribute("href") || "#").trim();
        if (itemUrl.startsWith("/")) {
          itemUrl = `${window.location.origin}${itemUrl}`;
        }

        const itemKey = `${name.toLowerCase()}|${itemUrl}`;
        if (existingItems.has(itemKey)) return;

        existingItems.add(itemKey);
        schema.mainEntity.itemListElement.push({
          "@type": "ListItem",
          position: schema.mainEntity.itemListElement.length + 1,
          name,
          description,
          url: itemUrl || "#",
        });
      });
    }
  }

  return schema;
}

export function generateItemListSchemaFromColumns(description) {
  const appItems = Array.from(document.querySelectorAll(".app-item"));

  if (appItems.length) {
    const itemListElement = [];

    appItems.forEach(item => {
      const heading = item.querySelector(".app-title, h2, h3");
      const title = cleanText(heading?.textContent || "");

      const paragraphs = Array.from(item.querySelectorAll(".app-text, p"));
      const descriptionText = cleanText(
        paragraphs
          .map(p => cleanText(p.textContent || ""))
          .filter(Boolean)
          .join(" "),
      );

      const firstLink = item.querySelector("a[href]");
      let href = firstLink ? firstLink.getAttribute("href") || "" : "";

      if (href.startsWith("/")) {
        href = `${window.location.origin}${href}`;
      }

      if (!href && heading?.id) {
        href = `${window.location.href.split("#")[0]}#${heading.id}`;
      }

      if (!title && !descriptionText) return;

      itemListElement.push({
        "@type": "ListItem",
        position: itemListElement.length + 1,
        name: title || descriptionText,
        description: descriptionText || title,
        url: href || window.location.href,
      });
    });

    if (!itemListElement.length) return null;

    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: description,
      itemListElement,
    };
  }

  const columns = document.querySelectorAll(".column, .container__col");
  if (!columns.length) return null;

  const itemListElement = [];

  const isSection = col => {
    const hasHeading = col.querySelector("h2, h3");
    const hasParagraph = col.querySelector("p, .listing__list-subtext");
    const hasLink = col.querySelector("a");
    return hasHeading && hasParagraph && hasLink;
  };

  const filteredColumns = Array.from(columns).filter(isSection);

  filteredColumns.forEach((column, idx) => {
    const heading = column.querySelector("h2, h3");
    const title = heading ? heading.textContent.trim() : "Default Title";

    const paragraphs = Array.from(
      column.querySelectorAll("p, .listing__list-subtext"),
    );
    const descriptionText = paragraphs
      .map(p => p.textContent.trim())
      .filter(Boolean)
      .join(". ");

    const link = column.querySelector("a[href]");
    let href = link ? link.getAttribute("href") : "#";

    if (href.startsWith("/")) {
      href = `${window.location.origin}${href}`;
    }

    itemListElement.push({
      "@type": "ListItem",
      position: idx + 1,
      name: title,
      description: descriptionText,
      url: href,
    });
  });

  if (!itemListElement.length) return null;

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: description,
    itemListElement,
  };
}

export function generateWebinarEventSchema() {
  const url = window.location.href;
  if (!url.includes("webinars")) return null;

  const schemaInfo = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: getMetadata("og:title") || "Default Title",
    description: (() => {
      const descEl =
        document.querySelector(".container__col--left .rich-text p") ||
        document.querySelector(".rich-text.js-rich-text p") ||
        document.querySelector(".rich-text p") ||
        document.querySelector(".columns > div > div");
      const text = cleanText(descEl?.textContent || descEl?.innerText || "");
      return text || getMetadata("og:description") || "";
    })(),
    url: getMetadata("og:url") || window.location.href,
    image: {
      "@type": "ImageObject",
      url: getMetadata("og:image"),
    },
    startDate: toISODate(
      getMetadata("modified-date") ||
        getMetadata("publication-date") ||
        document.lastModified,
    ),
    location: "Online",
  };

  if (url.endsWith("registration")) {
    observeDynamicContent(".container__col, .list.two-column", () => {
      const blocks = Array.from(document.querySelectorAll(".container__col"));

      schemaInfo.performer = blocks
        .map(block => {
          let name = "";
          let jobTitle = "";
          let image = "";
          let description = "";

          const contentRoot = block.querySelector(".rich-text") || block;

          const paragraphs = Array.from(
            contentRoot.querySelectorAll("p.rich-text--white-text, p"),
          );

          const imgEl = contentRoot.querySelector("img");
          if (imgEl) {
            image = imgEl.getAttribute("src") || "";
          }

          const leadParagraph = paragraphs.find(p => {
            const strongEl = p.querySelector("strong");
            const strongText = cleanText(strongEl?.textContent || "");
            return Boolean(strongText);
          });

          if (!leadParagraph) return null;

          const strongEl = leadParagraph.querySelector("strong");
          if (strongEl) {
            name = cleanText(strongEl.textContent).replace(/^"+|"+$/g, "");
          }

          const leadHTML = leadParagraph.innerHTML || "";

          const postStrongMatch = leadHTML.match(
            /<\/strong>\s*<br[^>]*>\s*"?([^"<]+)"?/i,
          );

          if (postStrongMatch) {
            jobTitle = cleanText(postStrongMatch[1]).replace(/^"+|"+$/g, "");
          } else {
            const brMatch = leadHTML.match(/<br[^>]*>\s*([^<]+)/i);
            if (brMatch) {
              jobTitle = cleanText(brMatch[1]).replace(/^"+|"+$/g, "");
            }
          }

          const leadParagraphIndex = paragraphs.indexOf(leadParagraph);

          const bodyParagraphs = paragraphs.slice(leadParagraphIndex + 1);

          description = bodyParagraphs
            .map(p => cleanText(p.textContent || ""))
            .filter(Boolean)
            .join(" ");

          if (!name && !jobTitle && !description) return null;

          return {
            "@type": "Person",
            name: name || "",
            jobTitle: jobTitle || "",
            image: image || "",
            description: description || "",
          };
        })
        .filter(Boolean)
        .filter(
          (person, index, arr) =>
            arr.findIndex(
              candidate =>
                candidate.name === person.name &&
                candidate.jobTitle === person.jobTitle,
            ) === index,
        );

      upsertSchemaScript("legacy-aem-event-schema", schemaInfo);
    });
  }

  return schemaInfo;
}

export async function generateOfficeDetailsLocalBusinessSchema() {
  const cityStateZip =
    document.querySelector(
      ".office-information__address .citystatezip, .address-info .citystatezip",
    )?.innerText || "";
  const [cityPart = "", regionZipPart = ""] = cityStateZip
    .split(",")
    .map(part => part.trim());
  const [regionPart = "", postalCodePart = ""] = regionZipPart.split(" ");

  const telephone =
    document.querySelector(
      ".office-information__telephone-numbers .telephone, .telephone-numbers [property='telephone'], .telephone-numbers .telephone",
    )?.innerText || "";

  const openingHoursText =
    document.querySelector(
      ".office-information__hours-of-operation .office-hours__list li, .hours-of-operation ul li",
    )?.innerText || "";

  const mapLink =
    document
      .querySelector(".office-information__map-link a[href], .map-link a[href]")
      ?.getAttribute("href") || "";

  const geoCoordinates = mapLink
    ? await getGeoCoordinates(mapLink)
    : { latitude: "", longitude: "" };

  const member = Array.from(
    document.querySelectorAll(
      ".people-listing__card, .people-cards .person-detail-card-wrapper",
    ),
  )
    .map(personCard => {
      const name =
        personCard.querySelector(".people-listing__name")?.innerText?.trim() ||
        personCard.querySelector(".display-name")?.innerText?.trim() ||
        "";

      const jobTitleFromListing = Array.from(
        personCard.querySelectorAll(".people-listing__position p"),
      )
        .map(el => cleanText(el.textContent))
        .filter(Boolean)
        .join(", ");

      const jobTitle =
        jobTitleFromListing ||
        personCard.querySelector(".job-title h4")?.innerText?.trim() ||
        "";

      const emailLink =
        personCard.querySelector(
          ".people-listing__bio-link[href^='mailto:'], .email a[href^='mailto:']",
        ) || null;

      const email = emailLink
        ? (emailLink.getAttribute("href") || "")
            .replace("mailto:", "")
            .trim() || emailLink.innerText.trim()
        : "";

      if (!name && !jobTitle && !email) return null;

      return {
        "@type": "Person",
        name,
        jobTitle,
        email,
      };
    })
    .filter(Boolean);

  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name:
      document.querySelector(".office-information__title h2, .office-info h2")
        ?.innerText || "Stewart Title Company",
    url: getMetadata("og:url") || "https://www.stewart.com/",
    image: {
      "@type": "ImageObject",
      url: getMetadata("og:image") || "",
    },
    founder: {
      "@type": "Organization",
      name: "Stewart Title",
      url: "https://www.stewart.com",
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: `${
        document.querySelector(
          ".office-information__address .street-address, .address-info .street-address",
        )?.innerText || ""
      } ${
        document.querySelector(
          ".office-information__address .street-address2, .address-info .street-address2",
        )?.innerText || ""
      }`.trim(),
      addressLocality: cityPart,
      addressRegion: regionPart,
      postalCode: postalCodePart,
      addressCountry: "US",
    },
    telephone,
    openingHours: cleanText(openingHoursText),
    geo: {
      "@type": "GeoCoordinates",
      latitude: geoCoordinates.latitude,
      longitude: geoCoordinates.longitude,
    },
    member,
  };
}

export async function generateLocalBusinessItemListSchema() {
  const offices = document.querySelectorAll(
    ".locator-results-list .office-listing-card",
  );
  if (!offices.length) return null;

  const itemListElement = await Promise.all(
    Array.from(offices).map(async (office, index) => {
      const mapLink =
        office
          .querySelector(
            ".office-listing-details a[href*='maps'], .office-listing-map a[href*='maps']",
          )
          ?.getAttribute("href") || "";

      const geoCoordinates = mapLink
        ? await getGeoCoordinates(mapLink)
        : { latitude: "", longitude: "" };

      return {
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "LocalBusiness",
          name: office.dataset.officeDisplayName || "Office Name",
          address: {
            "@type": "PostalAddress",
            streetAddress: `${office.dataset.officeAddress1 || ""} ${
              office.dataset.officeAddress2 || ""
            }`.trim(),
            addressLocality: office.dataset.city || "",
            addressRegion: office.dataset.state || "",
            postalCode: office.dataset.zip || "",
            addressCountry: "US",
          },
          telephone:
            office
              .querySelector(".office-listing-phone-number")
              ?.textContent.replace("ph. ", "")
              .trim() || "",
          geo: {
            "@type": "GeoCoordinates",
            latitude: geoCoordinates.latitude,
            longitude: geoCoordinates.longitude,
          },
        },
      };
    }),
  );

  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: getMetadata("og:title") || document.title || "Default Title",
    url: getMetadata("og:url") || window.location.href,
    description: getMetadata("og:description") || "",
    image: {
      "@type": "ImageObject",
      url: getMetadata("og:image") || "",
    },
    provider: {
      "@type": "Organization",
      name: "Stewart Title",
      url: "https://www.stewart.com",
    },
    mainEntity: {
      "@type": "ItemList",
      name:
        document.querySelector(".locator-results")?.dataset.city ||
        document.querySelector(".locator-results")?.dataset.market ||
        "City Name",
      itemListElement,
    },
  };
}

export async function addPageSchema() {
  const template = normalizeTemplate(getMetadata("template"));
  const url = window.location.href;

  const webinarEventSchema = generateWebinarEventSchema();
  if (webinarEventSchema) {
    upsertSchemaScript("legacy-aem-event-schema", webinarEventSchema);
    return webinarEventSchema;
  }

  let schemaInfo = {
    "@context": "https://schema.org",
  };

  let scriptId = "legacy-aem-page-schema";

  switch (template) {
    case "home-page":
      scriptId = "legacy-aem-website-schema";
      schemaInfo["@type"] = "WebSite";
      schemaInfo.url = "https://www.stewart.com/";
      schemaInfo.name = "Stewart Title";
      schemaInfo.potentialAction = [
        {
          "@type": "SearchAction",
          "query-input": "required name=search_term_string",
          target: {
            "@type": "EntryPoint",
            urlTemplate:
              "https://www.stewart.com/en/search?q={search_term_string}",
          },
        },
      ];
      break;

    case "press-release-page":
    case "blog-article-page":
      scriptId = "legacy-aem-newsarticle-schema";
      schemaInfo["@type"] = "NewsArticle";
      schemaInfo.headline = getMetadata("og:title");
      schemaInfo.image = {
        "@type": "ImageObject",
        url: getMetadata("og:image"),
      };
      schemaInfo.provider = {
        "@type": "Organization",
        name: "Stewart Title",
        url: "https://www.stewart.com",
      };
      schemaInfo.datePublished = getMetadata("publish-date");
      schemaInfo.dateModified = getMetadata("modified-date");
      schemaInfo.author = [
        {
          "@type": "Person",
          name: getMetadata("author") || "Stewart Title",
        },
      ];
      break;

    case "real-estate-dictionary-entry-page":
      scriptId = "legacy-aem-definedterm-schema";
      schemaInfo["@type"] = "DefinedTerm";
      schemaInfo.name = getMetadata("og:title");
      schemaInfo.description = getMetadata("description");
      schemaInfo.inDefinedTermSet =
        document.querySelector("link[rel='canonical']")?.href ||
        window.location.href;
      break;

    case "real-estate-dictionary-page": {
      scriptId = "legacy-aem-definedtermset-schema";
      schemaInfo["@type"] = "DefinedTermSet";
      schemaInfo.name = getMetadata("og:title");
      schemaInfo.description = getMetadata("description");
      schemaInfo.url =
        document.querySelector("link[rel='canonical']")?.href ||
        window.location.href;

      const definedTermsRaw = getMetadata("defined-terms");
      let definedTerms = [];

      if (definedTermsRaw) {
        try {
          const parsedTerms = JSON.parse(definedTermsRaw);
          if (Array.isArray(parsedTerms)) {
            definedTerms = parsedTerms;
          }
        } catch (error) {
          definedTerms = [];
        }
      }

      schemaInfo.inDefinedTermSet = definedTerms
        .map(term => {
          const name = cleanText(term?.name || "");
          const termUrl = cleanText(term?.url || "");

          if (!name || !termUrl) return null;

          return {
            "@type": "DefinedTerm",
            name,
            url: new URL(termUrl, window.location.origin).href,
          };
        })
        .filter(Boolean);
      break;
    }

    case "office-details-page": {
      scriptId = "legacy-aem-localbusiness-schema";
      schemaInfo = await generateOfficeDetailsLocalBusinessSchema();
      break;
    }

    case "market-landing-page": {
      if (
        url.endsWith("mexico") ||
        url.endsWith("bahamas") ||
        url.endsWith("puerto-rico")
      ) {
        scriptId = "legacy-aem-webpage-schema";
        observeDynamicContent("main", () => {
          schemaInfo = generateBasicWebPageSchema();
          upsertSchemaScript(scriptId, schemaInfo);
        });

        return schemaInfo;
      } else {
        scriptId = "legacy-aem-market-localbusiness-schema";
        schemaInfo =
          (await generateLocalBusinessItemListSchema()) ||
          generateBasicWebPageSchema();
      }
      break;
    }

    case "press-release-parent-page": {
      scriptId = "legacy-aem-pressreleaseparent-schema";
      const buildPressReleaseParentSchema = async () => {
        const nextSchemaInfo = {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: getMetadata("og:title"),
          description: getMetadata("og:description"),
          url: window.location.href,
        };

        const { entries: newsEntries, totalResults } =
          await fetchQueryIndexSheet("news");

        nextSchemaInfo.numberOfItems = totalResults;
        nextSchemaInfo.itemListElement = newsEntries.map((entry, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": "NewsArticle",
            headline: entry.title || "",
            url: entry.path || "",
            image: {
              "@type": "ImageObject",
              url: entry.image || "",
            },
            description: entry.description || "",
            author: {
              "@type": "Person",
              name: entry.author || "",
            },
            datePublished: toISODate((entry.date || 0) * 1000),
            dateModified: toISODate((entry.lastModified || 0) * 1000),
          },
        }));

        schemaInfo = nextSchemaInfo;
        upsertSchemaScript(scriptId, schemaInfo);
      };

      await buildPressReleaseParentSchema();

      observeLegacyAemUrlChanges(
        "__legacyAemPressReleaseParentSchemaOnUrlChange",
        buildPressReleaseParentSchema,
      );
      break;
    }

    case "education-insights-point": {
      scriptId = "legacy-aem-educationinsightspoint-schema";
      const buildEducationInsightsPointSchema = async () => {
        const nextSchemaInfo = {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: getMetadata("og:title"),
          description: getMetadata("og:description"),
          url: window.location.href,
        };

        const { entries: blogEntries, totalResults } =
          await fetchQueryIndexSheet("blog");

        nextSchemaInfo.numberOfItems = totalResults;
        nextSchemaInfo.itemListElement = blogEntries.map((entry, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": "NewsArticle",
            headline: entry.title || "",
            url: entry.path || "",
            image: {
              "@type": "ImageObject",
              url: entry.image || "",
            },
            description: entry.description || "",
            author: {
              "@type": "Person",
              name: entry.author || "",
            },
            datePublished: toISODate((entry.date || 0) * 1000),
            dateModified: toISODate((entry.date || 0) * 1000),
          },
        }));

        schemaInfo = nextSchemaInfo;
        upsertSchemaScript(scriptId, schemaInfo);
      };

      await buildEducationInsightsPointSchema();

      observeLegacyAemUrlChanges(
        "__legacyAemEducationInsightsPointSchemaOnUrlChange",
        buildEducationInsightsPointSchema,
      );
      break;
    }

    case "landing-page": {
      if (url.includes("commercial-escrow")) {
        scriptId = "legacy-aem-commercial-escrow-schema";
        schemaInfo = generateBasicWebPageSchema();
        schemaInfo.mainEntity = [];

        const personSchemas = [];
        const columns = document.querySelectorAll(".column");

        columns.forEach(column => {
          const personSchema = createPersonSchemaFromElement(
            column,
            "Stewart Title Company",
          );
          if (personSchema.name && personSchema.email) {
            personSchemas.push(personSchema);
          }
        });

        if (personSchemas.length) {
          schemaInfo.mainEntity.push(personSchemas);
        } else {
          delete schemaInfo.mainEntity;
        }
      } else if (
        url.endsWith("mid-atlantic-consumer") ||
        url.endsWith("step-into-the-future") ||
        url.endsWith("customer360") ||
        url.endsWith("stc-national-ron")
      ) {
        scriptId = "legacy-aem-landing-page-schema";
        schemaInfo = generateSchemaForLandingPage();
      } else if (
        url.endsWith("more-than-title") ||
        url.endsWith("more-than-title-acuma24") ||
        url.endsWith("more-than-title-mba24")
      ) {
        scriptId = "legacy-aem-more-than-title-schema";
        schemaInfo = generateBasicWebPageSchema();
        const initialItemListSchema = generateAccordionItemListSchema(
          "Stewart Makes Lending Simple With End-to-End Solutions",
        );

        if (initialItemListSchema) {
          schemaInfo.mainEntity = initialItemListSchema;
        }

        observeDynamicContent("main", () => {
          const dynamicSchemaInfo = generateBasicWebPageSchema();
          const itemListSchema = generateAccordionItemListSchema(
            "Stewart Makes Lending Simple With End-to-End Solutions",
          );

          if (itemListSchema) {
            dynamicSchemaInfo.mainEntity = itemListSchema;
          }

          upsertSchemaScript(scriptId, dynamicSchemaInfo);
        });
      } else if (url.endsWith("top-apps-for-real-estate-agents")) {
        scriptId = "legacy-aem-columns-itemlist-schema";
        observeDynamicContent("main", () => {
          schemaInfo = generateBasicWebPageSchema();

          const itemListSchema = generateItemListSchemaFromColumns(
            "Top Apps for Real Estate Agents",
          );

          if (itemListSchema) {
            schemaInfo.mainEntity = {
              "@type": "ItemList",
              name: itemListSchema.name,
              itemListElement: itemListSchema.itemListElement,
            };
          }

          schemaInfo.subjectOf = createVideoSchema(
            "nhtBKHubIm8",
            "Top Apps for Real Estate Agents: Explained",
            "2020-08-31",
          );

          upsertSchemaScript(scriptId, schemaInfo);
        });

        return schemaInfo;
      } else if (url.endsWith("stewart-homeacuity")) {
        scriptId = "legacy-aem-homeacuity-schema";
        schemaInfo = generateBasicWebPageSchema();
        schemaInfo.mainEntity = {
          "@type": "ItemList",
          name: "Stewart HomeAcuity includes solutions for:",
          itemListElement: [],
        };
        schemaInfo = populateListItems(schemaInfo);
      } else {
        scriptId = "legacy-aem-webpage-schema";
        schemaInfo = generateBasicWebPageSchema();
      }

      break;
    }

    case "course-list-page": {
      observeDynamicContent(".course-detail, .accordion-item", cards => {
        const courseCards = cards.filter(card =>
          card.matches(".course-detail, .accordion-item"),
        );

        schemaInfo["@type"] = "ItemList";
        schemaInfo.name = "Classes";

        schemaInfo.itemListElement = courseCards.map((card, index) => {
          const feeText =
            card
              .querySelector(".fee, .title-container .fee")
              ?.textContent?.trim() || "";
          const feeMatch = feeText.match(/(\d+(\.\d{1,2})?)\s*([A-Z]{3})?/i);
          const feeValue = feeMatch ? feeMatch[1] : "0";
          const feeCurrency =
            feeMatch && feeMatch[3] ? feeMatch[3].toUpperCase() : "USD";

          // Extract date and leave as text (optional ISO conversion can be added)
          const startDateText =
            card.querySelector(".start-date")?.textContent?.trim() || "";

          const endDateText =
            card.querySelector(".end-date")?.textContent?.trim() || "";

          // Extract credits and ensure it's a number
          const creditsText =
            card.querySelector(".credits")?.textContent?.trim() || "";
          const creditsMatch = creditsText.match(/\d+/);
          const numberOfCredits = creditsMatch
            ? parseInt(creditsMatch[0], 10)
            : 0;

          const attendanceMode =
            card
              .querySelector(".attendance-mode")
              ?.textContent?.trim()
              .toLowerCase() || "online";

          const courseWorkloadValue = calculateCourseWorkload(
            startDateText,
            endDateText,
          );

          const dateElement = card.querySelector(".accordion-item__subtitle");
          let courseScheduleValue;
          if (dateElement) {
            const dateText = dateElement.textContent?.trim();
            if (typeof dateText === "string" && dateText.length > 0) {
              courseScheduleValue = extractCourseSchedule(dateText);
            }
          }

          const instructor = Array.from(
            card.querySelectorAll("em, p, span, li"),
          )
            .map(el => el.textContent?.trim() || "")
            .find(text => /^instructor\s*:/i.test(text));

          const instructorSource = instructor || "";
          const instructorName =
            instructorSource
              .replace(/^instructor\s*:\s*/i, "")
              .split(",")[0]
              .trim() || "Unknown Instructor";

          const preferredOfferLink = card.querySelector("a.btn.btn--primary");
          const offerHref =
            preferredOfferLink?.getAttribute("href")?.trim() || "";

          let offerUrl = "";
          if (offerHref) {
            if (offerHref.startsWith("//")) {
              offerUrl = `${window.location.protocol}${offerHref}`;
            } else if (offerHref.startsWith("/")) {
              offerUrl = `${window.location.origin}${offerHref}`;
            } else {
              offerUrl = offerHref;
            }
          }

          return {
            "@type": "ListItem",
            position: index + 1,
            item: {
              "@type": "Course",
              name:
                card
                  .querySelector(".title, .accordion-item__title")
                  ?.textContent?.trim() || "Unknown Course",
              description:
                card
                  .querySelector(".description, .additional-description")
                  ?.textContent?.trim() || "No description available",
              numberOfCredits,
              provider: {
                "@type": "Person",
                name: instructorName,
              },
              offers: {
                "@type": "Offer",
                price: feeValue,
                priceCurrency: feeCurrency,
                url: offerUrl,
                category: "Educational",
              },
              hasCourseInstance: {
                "@type": "CourseInstance",
                startDate: startDateText || "Unknown Date",
                endDate: endDateText || "Unknown Date",
                courseMode: attendanceMode,
                courseWorkload: courseWorkloadValue,
                courseSchedule: courseScheduleValue,
              },
            },
          };
        });

        upsertSchemaScript("legacy-aem-course-schema", schemaInfo);
      });
      break;
    }

    case "section-page": {
      if (
        url.endsWith("tools-and-resources") ||
        url.endsWith("central-tx-accountability-ratings") ||
        url.endsWith("webinars-and-white-papers") ||
        url.endsWith("/resources") ||
        url.endsWith("order-center") ||
        url.endsWith("search-order") ||
        url.endsWith("/solutions") ||
        url.endsWith("title-and-closing-services") ||
        url.endsWith("/services") ||
        url.endsWith("/en/customer-type/title-agents/other-services") ||
        url.endsWith("agent-solutions") ||
        url.endsWith("digital-solutions") ||
        url.endsWith("mortgage-originations") ||
        url.endsWith("processing-and-underwriting") ||
        url.endsWith("closing-journey") ||
        url.endsWith("marketing-resources") ||
        url.endsWith("technology-center-of-stewart-pakistan") ||
        url.endsWith("/en/customer-type/mortgage-lenders/other-services")
      ) {
        scriptId = "legacy-aem-webpageservices-schema";
        observeDynamicContent("main", () => {
          schemaInfo = generateWebPageAndServicesSchema();
          schemaInfo.mainEntity = {
            "@type": "ItemList",
            name: "Key Service Features",
            itemListElement: [],
          };
          schemaInfo = populateListItems(schemaInfo);

          if (
            url.endsWith(
              "/en/customer-type/commercial-title-closing-services/tools-and-resources",
            )
          ) {
            schemaInfo.subjectOf = createVideoSchema(
              "UaEBb4wVumY",
              "VU Tutorial.",
              "2021-11-20",
            );
          }

          if (url.endsWith("processing-and-underwriting")) {
            schemaInfo.subjectOf = createVimeoVideoSchema(
              1083544711,
              "Appraisal and Valuation for Mortgage Lenders",
              "https://i.vimeocdn.com/video/2014771851-907120674eb0bdedd4f09a1ef9524353d6a2a3e16a06aa2602488ac84d38229a-d?mw=1600&mh=900&q=70&quot;",
            );
          }

          upsertSchemaScript(scriptId, schemaInfo);
        });
        return schemaInfo;
      } else if (url.includes("/stewart-in-the-studio/")) {
        scriptId = "legacy-aem-webpage-schema";
        observeDynamicContent(".js-vimeo-video", () => {
          schemaInfo = generateBasicWebPageSchema();

          const vimeoId = document
            .querySelector(".js-vimeo-video")
            ?.getAttribute("data-vimeo-id");

          if (vimeoId) {
            schemaInfo.subjectOf = createVimeoVideoSchema(
              Number(vimeoId),
              getMetadata("og:description"),
              getMetadata("og:image"),
              undefined,
              getMetadata("og:title"),
            );
          }

          upsertSchemaScript(scriptId, schemaInfo);
        });
        return schemaInfo;
      } else if (
        url.endsWith("recoupment-counsel") ||
        url.endsWith("underwriting-counsel") ||
        url.endsWith("claims-counsel")
      ) {
        scriptId = "legacy-aem-webpage-schema";
        schemaInfo = generateBasicWebPageSchema();

        observeDynamicContent("main", () => {
          schemaInfo.mainEntity = [];
          const personSchemas = [];

          const columns = document.querySelectorAll(".column");
          columns.forEach(column => {
            const personSchema = createPersonSchemaFromElement(
              column,
              "Stewart Title Guaranty Company",
            );
            if (personSchema.name && personSchema.email) {
              personSchemas.push(personSchema);
            }
          });

          const paragraphs = document.querySelectorAll("p");
          paragraphs.forEach(paragraph => {
            const personSchema = createPersonSchemaFromElement(
              paragraph,
              "Stewart Title Guaranty Company",
            );
            if (personSchema.name && personSchema.email) {
              personSchemas.push(personSchema);
            }
          });

          if (personSchemas.length) {
            schemaInfo.mainEntity.push(personSchemas);
          } else {
            delete schemaInfo.mainEntity;
          }

          upsertSchemaScript(scriptId, schemaInfo);
        });

        return schemaInfo;
      } else if (
        url.endsWith("other-services") ||
        url.endsWith("1031-exchanges") ||
        url.endsWith("amortization-calculator") ||
        url.endsWith("mortgage-calculator") ||
        url.endsWith("stewart-academy") ||
        url.endsWith("/title-insurance") ||
        url.endsWith("closing-and-escrow")
      ) {
        scriptId = "legacy-aem-service-schema";
        schemaInfo = generateBasicWebPageSchema();
        schemaInfo["@type"] = "Service";

        if (url.endsWith("closing-and-escrow")) {
          schemaInfo.subjectOf = createVideoSchema(
            "HCsZmcgKcIg",
            "The Real Estate Closing Process | Stewart Title",
            "2021-01-30",
          );
        }

        observeDynamicContent(
          '.section[data-section-status="loaded"]',
          sections => {
            schemaInfo.isRelatedTo = sections
              .filter(
                section =>
                  section.querySelector("h2") &&
                  section.querySelector("a.button.primary"),
              )
              .map(section => {
                const titleElement = section.querySelector("h2");
                const descriptionElement = section.querySelector(
                  "p:not(.button-container p)",
                );
                const linkElement = section.querySelector("a.button.primary");

                return {
                  "@type": "Service",
                  name: titleElement
                    ? titleElement.textContent.trim()
                    : "Unknown Service",
                  description: descriptionElement
                    ? descriptionElement.textContent.trim()
                    : "No description available",
                  url: linkElement
                    ? new URL(linkElement.href, window.location.origin).href
                    : "",
                };
              });

            if (!schemaInfo.isRelatedTo || !schemaInfo.isRelatedTo.length) {
              delete schemaInfo.isRelatedTo;
            }

            upsertSchemaScript(scriptId, schemaInfo);
          },
        );

        return schemaInfo;
      } else if (
        url.endsWith("get-a-quote") ||
        url.endsWith("conventional") ||
        url.endsWith("fha") ||
        url.endsWith("owner-finance")
      ) {
        scriptId = "legacy-aem-calculator-webpage-schema";
        const areaServed =
          (getMetadata("og:url") || window.location.href)
            .split("/")
            .slice(-2, -1)[0] || "Unknown Area";

        const readableArea =
          areaServed.charAt(0).toUpperCase() + areaServed.slice(1);

        schemaInfo = {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: getMetadata("og:title") || "",
          description: getMetadata("og:description") || "",
          url: getMetadata("og:url") || window.location.href,
          image: getMetadata("og:image") || "",
          provider: {
            "@type": "Organization",
            name: "Stewart Title",
            url: "https://www.stewart.com/",
          },
          mainEntity: {
            "@type": "Service",
            name: `Stewart Title Calculator Service for Buyer, Seller, and Title Fee Estimates in ${readableArea}.`,
            description:
              "A comprehensive calculator service that helps buyers, sellers, and real estate professionals accurately estimate closing costs, net proceeds, and title & escrow fees for property transactions in " +
              `${readableArea}.`,
            serviceArea: areaServed.replace(/-/g, " ").toUpperCase(),
            offers: {
              "@type": "Offer",
              name: "Closing Cost, Net Proceeds, and Title Fee Estimator",
              description:
                "Easily calculate buyer closing costs, seller net proceeds, and title & escrow fees for property transactions, providing accurate estimates tailored to your needs in " +
                `${readableArea}.`,
            },
          },
        };
      } else if (
        url.endsWith("connect-with-us") ||
        url.endsWith("contact-us")
      ) {
        scriptId = "legacy-aem-contactpage-schema";
        schemaInfo = {
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: getMetadata("og:title") || "",
          description: getMetadata("og:description") || "",
          url: getMetadata("og:url") || window.location.href,
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": "https://www.stewart.com/",
          },
          publisher: {
            "@type": "Organization",
            name: "Stewart Title",
            logo: {
              "@type": "ImageObject",
              url: getMetadata("og:image") || "",
            },
          },
        };

        if (
          url.endsWith(
            "/en/customer-type/international-title-services/contact-us",
          )
        ) {
          observeDynamicContent(
            '.section[data-section-status="loaded"]',
            () => {
              schemaInfo.mainEntity = [];
              const globalOfficesList = buildItemList(
                "#reference-the-links-below-to-get-in-touch-with-a-representative-at-one-of-our-global-offices",
                "Global Office Representatives",
                "Contact information for global offices",
                ".teaser-list-item",
                "a",
              );
              if (globalOfficesList)
                schemaInfo.mainEntity.push(globalOfficesList);
              upsertSchemaScript(scriptId, schemaInfo);
            },
          );
          return schemaInfo;
        }
      } else if (url.endsWith("education-and-insights")) {
        scriptId = "legacy-aem-education-insights-schema";
        schemaInfo = {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: getMetadata("og:title") || "Default Title",
          description: getMetadata("og:description") || "Default Description",
          url: getMetadata("og:url") || url,
          image: {
            "@type": "ImageObject",
            url: getMetadata("og:image") || "",
          },
          provider: {
            "@type": "Organization",
            name: "Stewart Title",
            url: "https://www.stewart.com",
          },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: [],
          },
        };

        let itemPosition = 1;
        const processedSections = new Set();

        observeDynamicContent(
          '.section[data-section-status="loaded"]',
          sections => {
            const validSections = sections.filter(section => {
              const hasH2 = Boolean(section.querySelector("h2"));
              const hasP = Boolean(section.querySelector("p"));
              return hasH2 && hasP && !processedSections.has(section);
            });

            validSections.forEach(section => {
              processedSections.add(section);
              const headingEl = section.querySelector("h2");
              const paragraphEl = section.querySelector("p");
              const linkEl = section.querySelector("a");

              const heading = headingEl
                ? headingEl.textContent.trim()
                : "Unnamed Section";
              const description = paragraphEl
                ? paragraphEl.textContent.trim()
                : "No description available";

              let urlValue = "#";
              if (linkEl) {
                const hrefValue = linkEl.getAttribute("href") || "#";
                urlValue = hrefValue.startsWith("/")
                  ? window.location.origin + hrefValue
                  : hrefValue;
              }

              const listItem = {
                "@type": "ListItem",
                position: itemPosition++,
                name: heading,
                description,
                url: urlValue,
              };

              if (heading === "Top Mobile Apps for Real Estate Agents") {
                const embedId = "nhtBKHubIm8";
                listItem.subjectOf = {
                  "@type": "VideoObject",
                  name: "Content from Youtube",
                  description: "Top Apps for Real Estate Agents: Explained",
                  thumbnailUrl: `https://img.youtube.com/vi/${embedId}/hqdefault.jpg`,
                  embedUrl: `https://www.youtube.com/embed/${embedId}?playlist=${embedId}&origin=https://www.stewart.com&autoplay=0&loop=0&rel=0&enablejsapi=1`,
                  contentUrl: `https://www.youtube.com/watch?v=${embedId}`,
                  uploadDate: "2020-08-31",
                };
              }

              schemaInfo.mainEntity.itemListElement.push(listItem);
            });

            upsertSchemaScript(scriptId, schemaInfo);
          },
        );

        return schemaInfo;
      } else if (url.endsWith("webinars")) {
        scriptId = "legacy-aem-webinars-schema";
        schemaInfo = generateBasicWebPageSchema();
        schemaInfo.mainEntity = [];

        observeDynamicContent('.section[data-section-status="loaded"]', () => {
          schemaInfo.mainEntity = [];

          const registerList = buildItemList(
            "#register-for-a-live-title-tenets-webinar",
            "Register for a Live Title Tenets Webinar",
            "List of live webinars for Title Tenets",
          );
          if (registerList) schemaInfo.mainEntity.push(registerList);

          const webinarRecordingsList = buildItemList(
            "#access-webinar-recordings",
            "Access Webinar Recordings",
            "On-demand commercial title webinar recordings",
          );
          if (webinarRecordingsList)
            schemaInfo.mainEntity.push(webinarRecordingsList);

          const energyWebinarList = buildItemList(
            "#access-energy-webinar-recordings",
            "Access Energy Webinar Recordings",
            "Recorded webinars focused on energy transactions",
          );
          if (energyWebinarList) schemaInfo.mainEntity.push(energyWebinarList);

          upsertSchemaScript(scriptId, schemaInfo);
        });

        return schemaInfo;
      } else if (
        url.endsWith("regional-managers") ||
        url.endsWith("commercial-teams") ||
        url.endsWith("commercial-managers") ||
        url.endsWith(
          "/en/customer-type/commercial-title-closing-services/contact-us/underwriters",
        ) ||
        url.endsWith("meet-our-team") ||
        url.endsWith(
          "/en/customer-type/title-agents/contact-us/agency-services-contacts",
        )
      ) {
        scriptId = "legacy-aem-webpage-schema";
        schemaInfo = generateBasicWebPageSchema();

        observeDynamicContent(
          ".person-detail-card, .people-listing__card, .card.light",
          () => {
            schemaInfo.mainEntity = generatePeopleSchema();
            upsertSchemaScript(scriptId, schemaInfo);
          },
        );

        return schemaInfo;
      } else if (
        url.endsWith("about-stewart/careers") ||
        url.endsWith("culture-of-caring") ||
        url.endsWith("governance") ||
        url.endsWith("suppliers-and-vendors")
      ) {
        scriptId = "legacy-aem-sectionitemlist-schema";
        observeDynamicContent("main", () => {
          schemaInfo = createSectionItemList();
          schemaInfo = populateListItems(schemaInfo);

          if (url.endsWith("culture-of-caring")) {
            schemaInfo.subjectOf = createVideoSchema(
              "jTad9R-kfuU",
              "Inclusion and Belonging at Stewart",
              "2025-03-06",
            );
          }

          upsertSchemaScript(scriptId, schemaInfo);
        });
        return schemaInfo;
      } else if (url.endsWith("ind/careers")) {
        scriptId = "legacy-aem-webpage-schema";
        schemaInfo = generateBasicWebPageSchema();
      } else if (url.endsWith("corporate-leadership")) {
        scriptId = "legacy-aem-webpage-schema";
        schemaInfo = generateBasicWebPageSchema();

        observeDynamicContent("main", () => {
          schemaInfo.mainEntity = [];

          const boardOfDirectors = createListItemsFromColumns(
            ".column-1, .container__col--33.container__col--left",
            "Board of Directors",
          );
          if (boardOfDirectors) schemaInfo.mainEntity.push(boardOfDirectors);

          const committees = createListItemsFromColumns(
            ".column-2, .container__col--33.container__col--center",
            "Committees",
          );
          if (committees) schemaInfo.mainEntity.push(committees);

          const executiveTeam = createListItemsFromColumns(
            ".column-3, .container__col--33.container__col--right",
            "Executive Team",
          );
          if (executiveTeam) schemaInfo.mainEntity.push(executiveTeam);

          if (!schemaInfo.mainEntity.length) {
            delete schemaInfo.mainEntity;
          }

          upsertSchemaScript(scriptId, schemaInfo);
        });

        return schemaInfo;
      } else if (url.endsWith("find-an-office")) {
        scriptId = "legacy-aem-webpage-schema";
        schemaInfo = generateBasicWebPageSchema();

        observeDynamicContent('.section[data-section-status="loaded"]', () => {
          schemaInfo.mainEntity = [];
          const officeLocationsList = buildItemList(
            "#office-locations",
            "Office Locations",
            "List of our state office pages",
            ".button-list.block li",
            "a",
          );
          if (officeLocationsList)
            schemaInfo.mainEntity.push(officeLocationsList);
          upsertSchemaScript(scriptId, schemaInfo);
        });

        return schemaInfo;
      } else if (
        url.endsWith("faq") ||
        url.endsWith("sustainability-q-n-a") ||
        url.endsWith("faqs")
      ) {
        scriptId = "legacy-aem-faq-schema";
        observeDynamicContent("main", () => {
          schemaInfo = generateFAQSchema();
          upsertSchemaScript(scriptId, schemaInfo);
        });
        return schemaInfo;
      } else if (url.endsWith("education")) {
        scriptId = "legacy-aem-webpageservices-schema";
        schemaInfo = generateWebPageAndServicesSchema();
      } else if (
        url.endsWith("the-basic-steps-of-closing") ||
        url.endsWith("what-is-title-insurance")
      ) {
        scriptId = "legacy-aem-webpage-schema";
        schemaInfo = generateBasicWebPageSchema();

        if (url.endsWith("the-basic-steps-of-closing")) {
          schemaInfo.subjectOf = createVideoSchema(
            "HCsZmcgKcIg",
            "The Real Estate Closing Process | Stewart Title",
            "2021-01-30",
          );
        } else {
          schemaInfo.subjectOf = createVideoSchema(
            "ALA9QCyEpRw",
            "What is Title Insurance.",
            "2021-01-29",
          );
        }

        observeDynamicContent("main", () => {
          const itemListSchema = url.endsWith("the-basic-steps-of-closing")
            ? generateAccordionItemListSchema("Basic Steps of Closing")
            : generateAccordionItemListSchema("Title Insurance");
          if (itemListSchema) {
            schemaInfo.mainEntity = itemListSchema.mainEntity || itemListSchema;
          }
          upsertSchemaScript(scriptId, schemaInfo);
        });

        return schemaInfo;
      } else {
        scriptId = "legacy-aem-webpage-schema";
        schemaInfo = generateBasicWebPageSchema();

        if (url.endsWith("available-real-estate-classes")) {
          const paragraphText =
            document.querySelector(".default-content-wrapper p")?.textContent ||
            "";
          schemaInfo.description = [
            getMetadata("og:description"),
            paragraphText,
          ]
            .filter(Boolean)
            .join(" ");
        } else if (
          url.endsWith("real-estate-ce-classes") ||
          url.endsWith("hoa") ||
          url.endsWith("online-documents-and-forms")
        ) {
          schemaInfo.description =
            getMetadata("og:description") || "Default Description";
        } else if (url.endsWith("lenders-real-estate-pros")) {
          const regularParagraphs = document.querySelectorAll(
            ".default-content-wrapper p:not(.button-container)",
          );
          schemaInfo.description = Array.from(regularParagraphs)
            .map(p => p.textContent.trim())
            .join(" ");
        }

        if (url.endsWith("/energy")) {
          observeDynamicContent(
            '.section[data-section-status="loaded"]',
            () => {
              schemaInfo.mainEntity = [];
              const energyWebinarList = buildItemList(
                "#energy-webinars",
                "Energy Webinars",
                "List of recorded energy transaction webinars",
              );
              if (energyWebinarList)
                schemaInfo.mainEntity.push(energyWebinarList);
              upsertSchemaScript(scriptId, schemaInfo);
            },
          );
        }

        if (url.endsWith("stewart-forever-farm")) {
          schemaInfo.subjectOf = createVideoSchema(
            "jjuxNR1hUFQ",
            "Stewart Title’s Forever Farm: Take Your Target Farming to the Next Level with the Most Advanced Tool in the Industry.",
            "2020-08-31",
          );
        }
        if (url.endsWith("lendingqb")) {
          schemaInfo.subjectOf = createVideoSchema(
            "rU5qzHu8EYs",
            "Stewart is now integrated with LENDINGQB, an industry leader in customized loan originations. Watch the video to see how it works and how it can help streamline your process.",
            "2020-08-31",
          );
        }
        if (url.endsWith("what-does-title-insurance-protect-you-from-a-lot")) {
          schemaInfo.subjectOf = createVideoSchema(
            "B4TGuf1nvyE",
            "What Does Title Insurance Protect You From - Special Tax Assessments.",
            "2020-08-31",
          );
        }
        if (url.endsWith("beverly-carter-story")) {
          schemaInfo.subjectOf = createVideoSchema(
            "wF4-UZEx864",
            "The Beverly Carter Story.",
            "2020-08-31",
          );
        }
        if (url.endsWith("calyx")) {
          schemaInfo.subjectOf = createVideoSchema(
            "1AKazQ4bFkw",
            "Calyx Demo",
            "2020-08-30",
          );
        }
        if (url.endsWith("encompass")) {
          schemaInfo.subjectOf = createVideoSchema(
            "qkGb5rTAlHM",
            "Encompass Demonstration",
            "2020-09-17",
          );
        }

        observeDynamicContent("main", () => {
          if (
            url.endsWith("underwriting-services") ||
            url.endsWith("stp-standards") ||
            url.endsWith("closing-and-funding") ||
            url.endsWith("stewart-accelerate") ||
            url.endsWith("centralized-title-and-settlement-services") ||
            url.endsWith("products-and-services") ||
            url.endsWith("financial-services")
          ) {
            schemaInfo.mainEntity = {
              "@type": "ItemList",
              name: "Key Service Features",
              itemListElement: [],
            };

            schemaInfo = populateListItems(schemaInfo);

            if (url.endsWith("stewart-accelerate")) {
              schemaInfo.subjectOf = createVideoSchema(
                "PDNxtMjWH0c",
                getMetadata("og:description"),
                "2021-05-06",
              );
            }

            upsertSchemaScript(scriptId, schemaInfo);
          }
        });

        observeDynamicContent("main", () => {
          const normalizedUrl = url.replace(/\/$/, "");
          if (normalizedUrl.endsWith("/servicing")) {
            const itemListSchema = generateAccordionItemListSchema(
              "Maximize Productivity with Customized Solutions",
            );
            if (itemListSchema) {
              schemaInfo.mainEntity =
                itemListSchema.mainEntity || itemListSchema;
            }
            upsertSchemaScript(scriptId, schemaInfo);
          }
        });
      }

      break;
    }

    case "state-landing-page":
    case "section-landing-page": {
      if (
        url.endsWith("real-estate-closing-title-services") ||
        url.endsWith("other-services") ||
        url.endsWith("other-services-for-real-estate-agents-and-brokers") ||
        url.includes("relocation-title-insurance-services") ||
        url.includes("title-insurance-closing-services") ||
        url.endsWith("-agents") ||
        url.endsWith("homebuilder-real-estate-closing-services") ||
        url.endsWith("international-title-services") ||
        url.endsWith("home-equity-services") ||
        url.endsWith("what-we-offer") ||
        url.endsWith("underwriting")
      ) {
        scriptId = "legacy-aem-webpageservices-schema";
        schemaInfo = generateWebPageAndServicesSchema();

        observeDynamicContent("main", () => {
          if (
            url.endsWith("other-services-for-real-estate-agents-and-brokers")
          ) {
            schemaInfo = generateWebPageAndServicesSchema();
          } else {
            schemaInfo.mainEntity = {
              "@type": "ItemList",
              name: "Key Service Features",
              itemListElement: [],
            };

            schemaInfo = populateListItems(schemaInfo);
          }

          upsertSchemaScript(scriptId, schemaInfo);
        });

        return schemaInfo;
      } else if (url.endsWith("insurance")) {
        scriptId = "legacy-aem-service-schema";
        schemaInfo = generateBasicWebPageSchema();
        schemaInfo["@type"] = "Service";
      } else if (url.endsWith("about-stewart")) {
        scriptId = "legacy-aem-sectionitemlist-schema";
        observeDynamicContent("main", () => {
          schemaInfo = createSectionItemList();
          schemaInfo = populateListItems(schemaInfo);
          upsertSchemaScript(scriptId, schemaInfo);
        });
        return schemaInfo;
      } else if (url.endsWith("fincen-aml")) {
        scriptId = "legacy-aem-fincen-schema";
        schemaInfo = generateSchemaForFinCen();
      } else if (url.endsWith("mortgage-lenders")) {
        scriptId = "legacy-aem-mortgagelenders-schema";
        schemaInfo = generateSchemaForMortgageLenders();
      } else {
        scriptId = "legacy-aem-webpage-schema";
        schemaInfo = generateBasicWebPageSchema();

        if (url.endsWith("/en/quick-links/safety")) {
          schemaInfo.subjectOf = createVideoSchema(
            "sqRaUsziVgU",
            "Introduction to The Beverly Carter Foundation.",
            "2020-08-31",
          );
        }

        observeDynamicContent("main", () => {
          if (
            url.endsWith("/en/customer-type/commercial-title-closing-services")
          ) {
            schemaInfo.mainEntity = {
              "@type": "ItemList",
              name: "Key Service Features",
              itemListElement: [],
            };
            schemaInfo = populateListItems(schemaInfo);
          } else {
            let linkElements = document.querySelectorAll(
              '.section[data-section-status="loaded"] .list.block ul li.list-item.with-icon a.icon-link',
            );

            if (!linkElements.length && url.endsWith("/servicing")) {
              linkElements = document.querySelectorAll(
                '.section[data-section-status="loaded"] .list.block ul li.list-item.with-icon',
              );
            }

            const items = Array.from(linkElements).map((link, index) => {
              const href = new URL(
                link.getAttribute("href"),
                window.location.origin,
              ).href;
              const headingEl = link.querySelector("h4");
              const descriptionEl = link.querySelector("p");
              return {
                "@type": "ListItem",
                position: index + 1,
                name: headingEl ? headingEl.textContent.trim() : "",
                url: href,
                description: descriptionEl
                  ? descriptionEl.textContent.trim()
                  : "",
              };
            });

            if (items.length > 0) {
              schemaInfo.mainEntity = {
                "@type": "ItemList",
                itemListElement: items,
              };
            }
          }

          if (url.endsWith("/en/pk")) {
            schemaInfo.subjectOf = createVideoSchema(
              "YvPq3zm5QNA",
              "Employee Appreciation Day 2021.",
              "2021-03-04",
            );
          }

          upsertSchemaScript(scriptId, schemaInfo);
        });
        return schemaInfo;
      }
      break;
    }

    case "insights-all": {
      scriptId = "legacy-aem-insights-all-schema";
      const buildInsightsAllSchema = async () => {
        const nextSchemaInfo = {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: getMetadata("og:title"),
          description: getMetadata("og:description"),
          url: window.location.href,
        };

        try {
          const { results: entries, totalResults } =
            await fetchFilteredSearchResults();
          if (entries.length) {
            nextSchemaInfo.numberOfItems = totalResults || entries.length;
            nextSchemaInfo.itemListElement = entries.map((p, index) => {
              const item = {
                "@type": "NewsArticle",
                headline: p.title || "",
                url: p.contentUrl
                  ? p.contentUrl.startsWith("/")
                    ? `${window.location.origin}${p.contentUrl}`
                    : p.contentUrl
                  : "",
                description: p.body
                  ? cleanText(p.body.replace(/<[^>]+>/g, ""))
                  : "",
              };

              if (p.date) item.datePublished = new Date(p.date).toISOString();
              if (p.date) item.dateModified = new Date(p.date).toISOString();
              if (p.authors?.length) {
                item.author = p.authors.map(name => ({
                  "@type": "Person",
                  name,
                }));
              }
              if (p.imageSrc) {
                item.image = { "@type": "ImageObject", url: p.imageSrc };
              }

              return {
                "@type": "ListItem",
                position: index + 1,
                item,
              };
            });
          }
        } catch (e) {}

        schemaInfo = nextSchemaInfo;
        upsertSchemaScript(scriptId, schemaInfo);
      };

      await buildInsightsAllSchema();

      observeLegacyAemUrlChanges(
        "__legacyAemInsightsAllSchemaOnUrlChange",
        buildInsightsAllSchema,
      );
      break;
    }

    default: {
      if (
        url.endsWith("faq") ||
        url.endsWith("sustainability-q-n-a") ||
        url.endsWith("faqs")
      ) {
        scriptId = "legacy-aem-faq-schema";
        schemaInfo = generateFAQSchema();
      } else if (
        url.endsWith("the-basic-steps-of-closing") ||
        url.endsWith("what-is-title-insurance")
      ) {
        scriptId = "legacy-aem-accordion-itemlist-schema";
        schemaInfo = url.endsWith("the-basic-steps-of-closing")
          ? generateAccordionItemListSchema("Basic Steps of Closing")
          : generateAccordionItemListSchema("Title Insurance");
      } else if (url.endsWith("culture-of-caring")) {
        scriptId = "legacy-aem-sectionitemlist-schema";
        observeDynamicContent("main", () => {
          schemaInfo = createSectionItemList();
          schemaInfo = populateListItems(schemaInfo);
          schemaInfo.subjectOf = createVideoSchema(
            "jTad9R-kfuU",
            "Inclusion and Belonging at Stewart",
            "2025-03-06",
          );
          upsertSchemaScript(scriptId, schemaInfo);
        });
        return schemaInfo;
      } else if (
        url.endsWith("about-stewart/careers") ||
        url.endsWith("governance") ||
        url.endsWith("suppliers-and-vendors")
      ) {
        scriptId = "legacy-aem-sectionitemlist-schema";
        schemaInfo = createSectionItemList();
        schemaInfo = populateListItems(schemaInfo);
      } else {
        scriptId = "legacy-aem-webpage-schema";
        schemaInfo = generateBasicWebPageSchema();
      }

      break;
    }
  }

  if (schemaInfo) {
    upsertSchemaScript(scriptId, schemaInfo);
  }

  return schemaInfo;
}
